"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3, ListChecks, SquareArrowOutUpRight } from "lucide-react";
import Link from "next/link";
import { getTprDepartmentName } from "@/configuration/groups/tpr/athena";
import { TprCoverageContinueButton } from "@/configuration/groups/tpr/components/coverage-continue-button";
import { TprManualStepContinueButton } from "@/configuration/groups/tpr/components/manual-step-continue-button";
import { TprOpsContinueButton } from "@/configuration/groups/tpr/components/ops-continue-button";
import { TprReceivingAssignmentContinueButton } from "@/configuration/groups/tpr/components/receiving-assignment-continue-button";
import { TprSyncAuthButtons } from "@/configuration/groups/tpr/components/sync-auth-buttons";
import { isTprSpecialCaseInsurance } from "@/configuration/groups/tpr/insurance-special-cases";
import { TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION } from "@/configuration/groups/tpr/referral-auth-instructions";
import { tprReferralAuthNeedsPendingUpload, tprReferralAuthNeedsReview } from "@/configuration/groups/tpr/workflows/referral-auth-status";
import type { StediEligibilityDisplayLabel, TprSyncChecklistTraceEntry, TprSyncChecklistUrgency } from "@/configuration/groups/tpr/workflows/sync-checklist";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { AthenaCoverageEvidence } from "@/src/components/tasks/approval-types/athena-coverage-evidence";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { TaskSchedulingRecommendation } from "@/src/components/tasks/task-scheduling-recommendation";
import { VerificationButtons } from "@/src/components/tasks/verification-buttons";
import { ValerieCard } from "@/src/components/valerie-card";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { formatDateMmDdYyyy, formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import type { ReceivingAssignmentOutput } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput, TprReferralAuthorizationOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { getFormattedAddress } from "@/src/models/address";
import type { Appointment } from "@/src/models/appointment";
import { Coverage, getCoverageDisplayName } from "@/src/models/coverage";
import { type DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { getPatientDisplayName, getPatientFaxNumber, getPatientName, getPatientPhoneNumber, type Patient } from "@/src/models/patient";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskStatus, TaskType } from "@/src/models/task";
import { ApprovalType, type TaskApproval } from "@/src/models/task-approval";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";
const NOT_AVAILABLE = "N/A";

/**
 * Lowercased opening segment every TPR disposition note carries — see `getUnifiedReferralInternalNote`
 * in configuration/groups/tpr/workflows/referral-notes.ts. Matched anywhere in the note (not just at
 * the start) because Athena appends internal notes server-side, so ours can end up after an earlier
 * one. Used to tell "Athena already has our note" apart from "Athena has some other note" — e.g.
 * `careTeamStep`'s referring-provider address-mismatch note, which is written earlier and carries no
 * such segment.
 */
const REVIEWED_BY_VALERIE_HEALTH_MARKER = "reviewed by valerie health";

// Plain string literals rather than importing the `TprSyncBucket` enum's runtime values: this is a
// "use client" component, and `sync-checklist.ts` transitively pulls in server-only Athena/GCP client
// code, so any real (non-type-only) import from it breaks the browser bundle. Values must match
// `TprSyncBucket` in configuration/groups/tpr/workflows/sync-checklist.ts exactly.
const TPR_MANUAL_REVIEW_BUCKET = "biltmorecardreferral";

/**
 * Plain-English labels for the 3 real destinations ops actually routes to. `TPR_MANUAL_REVIEW_BUCKET`
 * is deliberately excluded — it's the decision tree's "unable to auto-determine" catch-all, not a 4th
 * destination, so it gets its own "no confident suggestion" message instead of a label here.
 */
const SUGGESTED_BUCKET_LABELS: Record<string, string> = {
  closed: "Closed — upcoming appointment already scheduled",
  sscardio: "SS Cardio — stays with practice",
  biltmorecardiosched: "Biltmore — ready to schedule"
};
const NO_CONFIDENT_BUCKET_SUGGESTION = "No confident suggestion — ops must choose SS Cardio / Biltmore (ready to schedule) / Closed based on the details above.";

/** Renders provider name / NPI as independently copyable fields (no address — that's its own line now). */
function ProviderCopyFields(t0) {
  const $ = _c(9);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = provider && getPractitionerNameWithCredentials(provider) || NOT_AVAILABLE;
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  const npi = provider?.npiNumber || NOT_AVAILABLE;
  let t2;
  if ($[2] !== name) {
    t2 = <CopyableContent text={name} />;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== npi) {
    t3 = <CopyableContent text={npi} />;
    $[4] = npi;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <>Name: {t2}, NPI: {t3}</>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function ResolvedProviderCopyFields(t0) {
  const $ = _c(8);
  const {
    providerLabel,
    providerNpi,
    writeSafe
  } = t0;
  const t1 = providerLabel || NOT_AVAILABLE;
  let t2;
  if ($[0] !== t1) {
    t2 = <CopyableContent text={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = providerNpi || NOT_AVAILABLE;
  let t4;
  if ($[2] !== t3) {
    t4 = <CopyableContent text={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !writeSafe && " \u2014 verify manually before entry";
  let t6;
  if ($[4] !== t2 || $[5] !== t4 || $[6] !== t5) {
    t6 = <>Name: {t2}, NPI:{" "}{t4}{t5}</>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function getCoverageSummary(coverage: Coverage | null | undefined): string | null {
  if (!coverage) return null;
  const display = getCoverageDisplayName({
    coverage,
    includesPlan: true,
    includeSubscriberName: true
  });
  return coverage.subscriberId ? `${display} (Member ID: ${coverage.subscriberId})` : display;
}

/** One line per past appointment for the "Patient Type" sanity-check list, most-recent-first. */
function formatAppointmentHistoryEntry(appointment: Appointment): string {
  const date = appointment.appointmentDatetime ? formatHumanReadableDate(appointment.appointmentDatetime) : "TBD";
  const type = formatFromUnderscoreToHumanReadable(appointment.type) || NOT_AVAILABLE;
  const status = formatFromUnderscoreToHumanReadable(appointment.status || "") || NOT_AVAILABLE;
  return `${date} - ${type} (${status})`;
}
function sortAppointmentsMostRecentFirst(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((a, b) => {
    const aTime = a.appointmentDatetime ? new Date(a.appointmentDatetime).getTime() : 0;
    const bTime = b.appointmentDatetime ? new Date(b.appointmentDatetime).getTime() : 0;
    return bTime - aTime;
  });
}
type DataSourceEntry = {
  label: string;
  value: string;
  source: "Referral" | "Patient Chart" | "N/A";
  missing: boolean;
};

/**
 * A field only counts as truly missing if it's absent from BOTH the referral's extracted data AND
 * the patient's existing Athena chart — an extraction miss isn't "missing" if the chart already has
 * it. Prefers the referral's value when both are present, since that's what's freshest off the fax.
 */
function resolveDataSourceEntry(label: string, referralValue: string | null, chartValue: string | null): DataSourceEntry {
  if (referralValue) return {
    label,
    value: referralValue,
    source: "Referral",
    missing: false
  };
  if (chartValue) return {
    label,
    value: chartValue,
    source: "Patient Chart",
    missing: false
  };
  return {
    label,
    value: NOT_AVAILABLE,
    source: "N/A",
    missing: true
  };
}
type TimelineStatus = "automated" | "verified" | "verify" | "needs_action" | "failed" | "waiting" | "not_run" | "not_needed";
type TimelineStep = {
  number: number;
  title: string;
  status: TimelineStatus;
  summary: string;
  taskStepKeys: StepKey[];
  content?: React.ReactNode;
  continuation?: {
    instruction: string;
    control: React.ReactNode;
  };
};
export type DecisionMetadataItem = {
  label: string;
  value: React.ReactNode;
};
export type TprChartReferralAuth = {
  referralAuthId: string;
  number: string | null;
  startDate: string | null;
  endDate: string | null;
  expired: boolean | null;
};
export type TprChartRequestLetter = {
  documentEhrId: string;
  documentDate: string | null;
  description: string | null;
};
type TprOpsCrossCheck = {
  patientName: string;
  patientDateOfBirth: string;
  patientPhoneNumber: string;
  patientFaxNumber: string;
  patientEthnicity: string;
  patientRace: string;
  patientLanguage: string;
  patientHomeAddress: string;
  receivingProviderName: string;
  receivingProviderNpi: string;
  referringProviderName: string;
  referringProviderNpi: string;
  referringProviderSpecialty: string;
  referringLocationName: string;
  referringLocationAddress: string;
  emergencyContactName: string;
  emergencyContactPhoneNumber: string;
  emergencyContactRelationship: string;
  referralDate: string;
  primaryPlanLabel: string;
  primarySubscriberId: string;
  /** PIMC / Triwest / MercyCare — special-case payers ops wants visually flagged on Insurance. */
  isSpecialCaseInsurance: boolean;
  diagnosis: string;
};
export function DecisionMetadata(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="flex flex-wrap gap-2">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(item) {
  return <div key={item.label} className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"><dt className="text-muted-foreground">{item.label}</dt><dd className="font-medium text-foreground">{item.value}</dd></div>;
}
type ProviderSelectionEvidence = NonNullable<ReceivingAssignmentOutput["providerSelectionEvidence"]>;
type ProviderSelectionStep = NonNullable<ProviderSelectionEvidence["selectedStep"]>;
function providerSelectionReason(selectedStep: ProviderSelectionEvidence["selectedStep"]): string {
  switch (selectedStep) {
    case "upcoming_appointment":
      return "Selected from the earliest qualifying Biltmore appointment within 60 days.";
    case "past_appointment":
      return "Selected from the most recent qualifying completed Biltmore appointment in the past three years.";
    case "usual_provider":
      return "Legacy selection used the Athena usual provider; verify this assignment against appointment history.";
    case "referral_provider":
      return "Selected because this provider is named on the referral.";
    case "diagnosis_match":
      return "Selected because this provider treats the referral diagnosis at the chosen location.";
    default:
      return "No provider was selected automatically.";
  }
}
function ProviderSelectionChecklist(t0) {
  const $ = _c(35);
  const {
    evidence,
    upcomingAppointmentProviderSummary,
    referralProviderName,
    referralProviderNpi,
    diagnosis,
    selectedProviderName,
    selectedLocation
  } = t0;
  let t1;
  if ($[0] !== evidence.appointmentProviders) {
    t1 = evidence.appointmentProviders.some(_temp2);
    $[0] = evidence.appointmentProviders;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasQualificationEvidence = t1;
  let t2;
  if ($[2] !== evidence.appointmentProviders || $[3] !== evidence.selectedStep || $[4] !== hasQualificationEvidence || $[5] !== upcomingAppointmentProviderSummary) {
    t2 = period => {
      const providers = evidence.appointmentProviders.flatMap(provider_0 => {
        const count = period === "past" ? provider_0.qualifyingPastAppointmentCount ?? 0 : provider_0.qualifyingUpcomingAppointmentCount ?? 0;
        const locationEhrId = period === "past" ? provider_0.mostRecentPastLocationEhrId : provider_0.earliestUpcomingLocationEhrId;
        const location = locationEhrId ? getTprDepartmentName(locationEhrId) ?? `Athena department ${locationEhrId}` : null;
        return count > 0 ? [`${provider_0.providerName}${provider_0.providerNpi ? ` · ${provider_0.providerNpi}` : ""}${location ? ` · ${location}` : ""} (${count})`] : [];
      });
      if (providers.length > 0) {
        return providers.join(", ");
      }
      if (period === "upcoming" && !hasQualificationEvidence && upcomingAppointmentProviderSummary) {
        return upcomingAppointmentProviderSummary;
      }
      if (!hasQualificationEvidence && (evidence.selectedStep === "usual_provider" || evidence.appointmentProviders.length > 0)) {
        return "Not captured for this legacy task";
      }
      return "None";
    };
    $[2] = evidence.appointmentProviders;
    $[3] = evidence.selectedStep;
    $[4] = hasQualificationEvidence;
    $[5] = upcomingAppointmentProviderSummary;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const providerCountSummary = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[7] !== diagnosis || $[8] !== evidence.pastAppointmentCount || $[9] !== evidence.selectedStep || $[10] !== evidence.upcomingAppointmentCount || $[11] !== providerCountSummary || $[12] !== referralProviderName || $[13] !== referralProviderNpi || $[14] !== selectedLocation || $[15] !== selectedProviderName) {
    const steps = [{
      key: "upcoming_appointment",
      title: "Check for an upcoming appointment",
      items: [{
        label: "Upcoming within 60 days",
        value: evidence.upcomingAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("upcoming")
      }]
    }, {
      key: "past_appointment",
      title: "Check past appointments at Biltmore locations",
      items: [{
        label: "Completed in past 3 years",
        value: evidence.pastAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("past")
      }]
    }, {
      key: "referral_provider",
      title: "Use the provider named on the referral",
      items: [{
        label: "Provider",
        value: referralProviderName
      }, {
        label: "NPI",
        value: referralProviderNpi
      }]
    }, {
      key: "diagnosis_match",
      title: "Sanity-check the pick or find a replacement",
      items: [{
        label: "Diagnosis",
        value: diagnosis
      }, {
        label: "Location",
        value: selectedLocation
      }, {
        label: "Final provider",
        value: selectedProviderName
      }]
    }];
    t6 = "space-y-2 border-t pt-3";
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <summary className="cursor-pointer text-sm font-medium">Find the Provider and Location</summary>;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    t5 = "mt-2 space-y-2";
    t3 = "space-y-2";
    t4 = steps.map((step, index) => {
      const selected = evidence.selectedStep === step.key;
      return <li key={step.key} className={selected ? "space-y-2 rounded-md border border-success/40 bg-success/10 p-3" : "space-y-2 rounded-md border bg-muted/10 p-3"}><div className="flex items-center justify-between gap-3"><span className="text-sm font-medium">Step {index + 1} — {step.title}</span>{selected ? <Badge variant="outline" className="border-success/40 bg-success/10 text-success">Selected</Badge> : null}</div><DecisionMetadata items={selected ? [...step.items, {
          label: "Picked provider",
          value: selectedProviderName
        }, {
          label: "Reason",
          value: providerSelectionReason(evidence.selectedStep)
        }] : step.items} /></li>;
    });
    $[7] = diagnosis;
    $[8] = evidence.pastAppointmentCount;
    $[9] = evidence.selectedStep;
    $[10] = evidence.upcomingAppointmentCount;
    $[11] = providerCountSummary;
    $[12] = referralProviderName;
    $[13] = referralProviderNpi;
    $[14] = selectedLocation;
    $[15] = selectedProviderName;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t4) {
    t8 = <ol className={t3}>{t4}</ol>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== evidence.selectedStep) {
    t9 = evidence.selectedStep === "usual_provider" ? <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">This assignment was created with the retired Athena usual-provider rule. Verify the provider and location against qualifying appointment history before following it.</div> : null;
    $[25] = evidence.selectedStep;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t5 || $[28] !== t8 || $[29] !== t9) {
    t10 = <div className={t5}>{t8}{t9}</div>;
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t6 || $[33] !== t7) {
    t11 = <details className={t6}>{t7}{t10}</details>;
    $[31] = t10;
    $[32] = t6;
    $[33] = t7;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}
function _temp2(provider) {
  return provider.qualifyingUpcomingAppointmentCount !== undefined || provider.qualifyingPastAppointmentCount !== undefined;
}
const TIMELINE_STATUS = {
  automated: {
    label: "Automated",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verified: {
    label: "Verified",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verify: {
    label: "Verify",
    className: "border-warn/30 bg-warn/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  needs_action: {
    label: "Needs action",
    className: "border-warn-foreground/30 bg-warn-foreground/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  failed: {
    label: "Failed",
    className: "border-destructive-foreground/30 bg-destructive-foreground/10 text-destructive-foreground",
    icon: <CircleAlert />
  },
  waiting: {
    label: "Waiting",
    className: "border-border bg-muted text-muted-foreground",
    icon: <Clock3 />
  },
  not_run: {
    label: "Not run",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  },
  not_needed: {
    label: "Not needed",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  }
} satisfies Record<TimelineStatus, {
  label: string;
  className: string;
  icon: React.ReactNode;
}>;
function taskStepStatus(taskSteps: TaskStep[], keys: StepKey[]): TimelineStatus {
  const rows = keys.map(key => taskSteps.find(step => step.stepKey === key)).filter(Boolean);
  if (rows.some(row => row?.status === TaskStepStatus.FAILED)) return "failed";
  if (rows.some(row => row?.status === TaskStepStatus.ASYNC || row?.status === TaskStepStatus.PAUSED)) {
    return "waiting";
  }
  if (keys.every(key => taskSteps.some(step => step.stepKey === key && step.status === TaskStepStatus.COMPLETED))) {
    return "automated";
  }
  return "waiting";
}
function TimelineStepCard(t0) {
  const $ = _c(23);
  const {
    task,
    step,
    failedStepKey
  } = t0;
  const effectiveStatus = failedStepKey ? "failed" : step.status;
  const status = TIMELINE_STATUS[effectiveStatus];
  const isSkipped = effectiveStatus === "not_needed";
  let t1;
  if ($[0] !== failedStepKey || $[1] !== step.continuation || $[2] !== task) {
    t1 = failedStepKey ? {
      instruction: "After completing this step manually, continue with the remaining steps.",
      control: <TprManualStepContinueButton task={task} stepKey={failedStepKey} />
    } : step.continuation;
    $[0] = failedStepKey;
    $[1] = step.continuation;
    $[2] = task;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const continuation = t1;
  const cardVariant = effectiveStatus === "automated" || effectiveStatus === "verified" ? "success" : effectiveStatus === "failed" ? "destructive" : effectiveStatus === "needs_action" || effectiveStatus === "verify" ? "warn" : "outline";
  let t2;
  if ($[4] !== continuation) {
    t2 = continuation ? <div className="space-y-3 border-t pt-4"><p className="text-sm text-muted-foreground">{continuation.instruction}</p><div className="flex justify-center">{continuation.control}</div></div> : null;
    $[4] = continuation;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== step.content || $[7] !== t2) {
    t3 = <>{step.content}{t2}</>;
    $[6] = step.content;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const body = t3;
  const t4 = `${step.number}. ${step.title}`;
  const t5 = effectiveStatus === "automated" ? undefined : status.icon;
  const t6 = effectiveStatus === "not_run" || isSkipped ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined;
  let t7;
  if ($[9] !== effectiveStatus || $[10] !== status) {
    t7 = effectiveStatus === "automated" ? <span className="text-success-foreground" aria-label="Automated"><CheckCircleIcon weight="fill" className="size-5" /></span> : <Badge variant="outline" className={status.className}>{status.label}</Badge>;
    $[9] = effectiveStatus;
    $[10] = status;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== body || $[13] !== isSkipped || $[14] !== step.summary) {
    t8 = isSkipped ? <details><summary className="cursor-pointer text-sm text-muted-foreground">{step.summary}</summary><div className="mt-3 space-y-3">{body}</div></details> : <><p className="text-sm text-muted-foreground">{step.summary}</p>{body}</>;
    $[12] = body;
    $[13] = isSkipped;
    $[14] = step.summary;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== cardVariant || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t9 = <ValerieCard title={t4} icon={t5} className={t6} variant={cardVariant} actions={t7} contentClassName="space-y-3">{t8}</ValerieCard>;
    $[16] = cardVariant;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}
function TprStediEligibilityValues(t0) {
  const $ = _c(5);
  const {
    referralId
  } = t0;
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referralId);
  let t1;
  if ($[0] !== eligibilityCheck) {
    t1 = <StediEligibilitySummary check={eligibilityCheck} showHeader={false} />;
    $[0] = eligibilityCheck;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-sm text-muted-foreground">Recheck or edit coverage on the Information tab.</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="space-y-3">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function TprSyncTimeline(t0) {
  const $ = _c(456);
  const {
    task,
    referralId,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    resolvedReceiving,
    patientLabel,
    patientEhrId,
    documentId,
    bucketLabel,
    internalNoteToEnter,
    urgency,
    actionNote,
    decisionTrace,
    insuranceStatusDisplay,
    hasMissingInformation,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    upcomingAppointments,
    pastAppointments,
    documentUploadLabels,
    privacyNoticeOnFile,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    patientDisplayId,
    opsCrossCheck,
    chartReferralAuths,
    chartRequestLetters
  } = t0;
  let t1;
  if ($[0] !== taskSteps) {
    t1 = taskSteps.some(_temp3);
    $[0] = taskSteps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isProviderFirst = t1;
  let t2;
  if ($[2] !== pastAppointments || $[3] !== resolvedReceiving.providerSelectionEvidence || $[4] !== upcomingAppointments) {
    t2 = resolvedReceiving.providerSelectionEvidence ?? {
      selectedStep: null,
      upcomingAppointmentCount: upcomingAppointments.length,
      pastAppointmentCount: pastAppointments.length,
      appointmentProviders: []
    };
    $[2] = pastAppointments;
    $[3] = resolvedReceiving.providerSelectionEvidence;
    $[4] = upcomingAppointments;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const providerSelectionEvidence = t2;
  const workflowStillProcessing = task.status === TaskStatus.PROCESSING;
  let coverageApproval;
  let failedStepCard;
  let opsApproval;
  let steps;
  let t3;
  let waitingForAssociatedReferral;
  if ($[6] !== actionNote || $[7] !== bucketLabel || $[8] !== chartReferralAuths || $[9] !== chartRequestLetters || $[10] !== coverageReadback || $[11] !== decisionTrace || $[12] !== documentId || $[13] !== documentUploadLabels || $[14] !== followThroughEligible || $[15] !== hasMissingInformation || $[16] !== insuranceRequirements || $[17] !== insuranceStatusDisplay || $[18] !== internalNoteToEnter || $[19] !== isPrimaryCareProvider || $[20] !== isProviderFirst || $[21] !== opsCrossCheck.diagnosis || $[22] !== opsCrossCheck.emergencyContactName || $[23] !== opsCrossCheck.emergencyContactPhoneNumber || $[24] !== opsCrossCheck.emergencyContactRelationship || $[25] !== opsCrossCheck.isSpecialCaseInsurance || $[26] !== opsCrossCheck.patientDateOfBirth || $[27] !== opsCrossCheck.patientEthnicity || $[28] !== opsCrossCheck.patientFaxNumber || $[29] !== opsCrossCheck.patientHomeAddress || $[30] !== opsCrossCheck.patientLanguage || $[31] !== opsCrossCheck.patientName || $[32] !== opsCrossCheck.patientPhoneNumber || $[33] !== opsCrossCheck.patientRace || $[34] !== opsCrossCheck.primaryPlanLabel || $[35] !== opsCrossCheck.primarySubscriberId || $[36] !== opsCrossCheck.receivingProviderName || $[37] !== opsCrossCheck.receivingProviderNpi || $[38] !== opsCrossCheck.referralDate || $[39] !== opsCrossCheck.referringLocationAddress || $[40] !== opsCrossCheck.referringProviderName || $[41] !== opsCrossCheck.referringProviderNpi || $[42] !== opsCrossCheck.referringProviderSpecialty || $[43] !== patientDisplayId || $[44] !== patientEhrId || $[45] !== patientLabel || $[46] !== pendingApproval || $[47] !== primaryCareProviderAlreadyPresent || $[48] !== privacyNoticeOnFile || $[49] !== providerSelectionEvidence || $[50] !== receivingAssignmentConfirmed || $[51] !== referralAuthorization || $[52] !== referralId || $[53] !== resolvedReceiving.advisories || $[54] !== resolvedReceiving.flags || $[55] !== resolvedReceiving.locationLabel || $[56] !== resolvedReceiving.providerLabel || $[57] !== resolvedReceiving.providerNpi || $[58] !== resolvedReceiving.providerWriteSafe || $[59] !== resolvedReceiving.routedToSscardio || $[60] !== resolvedReceiving.usualProviderWriteSafe || $[61] !== task || $[62] !== taskSteps || $[63] !== unresolvedUpcomingProviders || $[64] !== upcomingAppointmentProviderSummary || $[65] !== urgency.isUrgent || $[66] !== urgency.reason || $[67] !== urgency.schedulingPriority || $[68] !== workflowStillProcessing) {
    const assignmentStatus = isProviderFirst ? taskStepStatus(taskSteps, [StepKey.RESOLVE_RECEIVING_ASSIGNMENT]) : workflowStillProcessing ? "waiting" : "not_run";
    const assignmentDependentStatus = assignmentStatus === "failed" ? "not_run" : assignmentStatus;
    const receivingAssignmentApproval = pendingApproval?.approvalType === ApprovalType.TPR_RECEIVING_ASSIGNMENT ? pendingApproval : null;
    const assignmentRequiresManualReview = assignmentStatus === "automated" && !resolvedReceiving.providerWriteSafe && receivingAssignmentApproval !== null;
    const usesRetiredUsualProviderRule = providerSelectionEvidence.selectedStep === "usual_provider";
    const assignmentNeedsManualConfirmation = !resolvedReceiving.providerWriteSafe || usesRetiredUsualProviderRule;
    const assignmentWasManuallyConfirmed = assignmentStatus === "automated" && assignmentNeedsManualConfirmation && receivingAssignmentConfirmed;
    const assignmentReviewStatus = assignmentWasManuallyConfirmed ? "verified" : assignmentRequiresManualReview || usesRetiredUsualProviderRule ? "needs_action" : assignmentDependentStatus;
    const gateStatus = taskStepStatus(taskSteps, [StepKey.TPR_CAMELBACK_FOLLOW_THROUGH]);
    const assignmentConfirmationStatus = assignmentWasManuallyConfirmed ? "verified" : assignmentReviewStatus === "needs_action" ? "needs_action" : assignmentStatus === "automated" ? gateStatus : assignmentStatus;
    const confirmedNonCamelbackAssignment = assignmentStatus === "automated" && !followThroughEligible && receivingAssignmentApproval === null;
    const skippedFollowThroughStatus = confirmedNonCamelbackAssignment ? "not_needed" : "not_run";
    const skippedFollowThroughSummary = assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : confirmedNonCamelbackAssignment ? "Not needed for the resolved non-Camelback receiving assignment." : "Not run because the receiving assignment has not completed.";
    coverageApproval = pendingApproval?.approvalType === ApprovalType.ATHENA_COVERAGE ? pendingApproval : null;
    opsApproval = pendingApproval?.approvalType === ApprovalType.TPR_REFERRAL_OPS ? pendingApproval : null;
    const opsKind = opsApproval?.metadata?.kind ?? null;
    let t4;
    if ($[75] !== taskSteps) {
      t4 = taskSteps.some(_temp4);
      $[75] = taskSteps;
      $[76] = t4;
    } else {
      t4 = $[76];
    }
    waitingForAssociatedReferral = t4;
    const coverageReadbackComplete = taskSteps.some(_temp5);
    let t5;
    if ($[77] !== taskSteps) {
      t5 = taskSteps.some(_temp6);
      $[77] = taskSteps;
      $[78] = t5;
    } else {
      t5 = $[78];
    }
    const authRequestUploaded = t5;
    let t6;
    if ($[79] !== taskSteps) {
      t6 = taskSteps.some(_temp7);
      $[79] = taskSteps;
      $[80] = t6;
    } else {
      t6 = $[80];
    }
    const notesComplete = t6;
    let t7;
    if ($[81] !== taskSteps) {
      t7 = taskSteps.some(_temp8);
      $[81] = taskSteps;
      $[82] = t7;
    } else {
      t7 = $[82];
    }
    const missingInfoUploadComplete = t7;
    const verificationActionStepNumber = task.status === TaskStatus.VERIFICATION_NEEDED ? 11 : null;
    let t8;
    if ($[83] !== followThroughEligible || $[84] !== skippedFollowThroughStatus || $[85] !== taskSteps) {
      t8 = keys => followThroughEligible ? taskStepStatus(taskSteps, keys) : skippedFollowThroughStatus;
      $[83] = followThroughEligible;
      $[84] = skippedFollowThroughStatus;
      $[85] = taskSteps;
      $[86] = t8;
    } else {
      t8 = $[86];
    }
    const followThroughStatus = t8;
    const resolveConsolidatedStatus = _temp9;
    let coverageStatus = assignmentRequiresManualReview ? "not_run" : taskStepStatus(taskSteps, [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE]);
    if (coverageApproval) {
      coverageStatus = coverageApproval.metadata?.reviewMode === "verify" ? "verify" : "needs_action";
    } else {
      if (coverageReadbackComplete) {
        coverageStatus = "verified";
      }
    }
    const persistedAthenaEligibilityStatus = followThroughStatus([StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY]);
    const athenaEligibilityStatus = coverageReadback?.primary.selfPay ? "not_needed" : persistedAthenaEligibilityStatus === "automated" && insuranceStatusDisplay === "Unverified" ? "needs_action" : persistedAthenaEligibilityStatus;
    const networkStatus = coverageReadback?.primary.selfPay || referralAuthorization?.outcome === "not_needed" && referralAuthorization.reason === "ineligible" ? "not_needed" : insuranceRequirements && (insuranceRequirements.clinicNetwork === "manual_review" || insuranceRequirements.providerNetwork === "manual_review" || insuranceRequirements.additionalSchedulingInformationRequired) ? "needs_action" : followThroughStatus([StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS]);
    const authorizationStatus = opsKind === "authorization_review" ? "verify" : opsKind === "manual_authorization" ? "needs_action" : tprReferralAuthNeedsReview(referralAuthorization) ? "needs_action" : referralAuthorization?.outcome === "not_needed" ? "not_needed" : referralAuthorization?.outcome === "needs_pending_request" ? "automated" : followThroughStatus([StepKey.TPR_REFERRAL_AUTHORIZATION]);
    let t9;
    if ($[87] !== referralAuthorization) {
      t9 = tprReferralAuthNeedsPendingUpload(referralAuthorization);
      $[87] = referralAuthorization;
      $[88] = t9;
    } else {
      t9 = $[88];
    }
    const referralAuthNeedsPendingUpload = t9;
    const pcpFaxStatus = opsKind === "pcp_referral_fax" ? "needs_action" : insuranceRequirements && !insuranceRequirements.referralMustBeFromPcp ? "not_needed" : followThroughStatus([StepKey.TPR_PCP_REFERRAL_FAX]);
    const authRequestUploadStatus = opsKind === "auth_request_upload" ? "needs_action" : referralAuthNeedsPendingUpload ? authRequestUploaded ? "automated" : followThroughStatus([StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD]) : "not_needed";
    const missingInfoFaxStatus = !hasMissingInformation ? "not_needed" : missingInfoUploadComplete ? "automated" : followThroughStatus([StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX]);
    const outboundFaxStatus = resolveConsolidatedStatus([authRequestUploadStatus, missingInfoFaxStatus]);
    const closeDocumentStatus = opsKind === "close_document" ? "needs_action" : followThroughStatus([StepKey.TPR_CLOSE_DOCUMENT]);
    let fullRegistrationReviewReasons;
    let t10;
    if ($[89] !== privacyNoticeOnFile || $[90] !== task.files) {
      const workflowNote = task.files.find(_temp10)?.note;
      const t11 = privacyNoticeOnFile === false || workflowNote?.includes("Athena privacy notice could not be set automatically") ? "Set Athena Privacy to YES manually." : null;
      let t12;
      if ($[93] !== t11) {
        t12 = [t11].filter(_temp11);
        $[93] = t11;
        $[94] = t12;
      } else {
        t12 = $[94];
      }
      fullRegistrationReviewReasons = t12;
      t10 = workflowNote?.includes("could not be matched in Athena and was not added to the patient's care team") ? "Add the unmatched referring provider to the Athena care team manually." : null;
      $[89] = privacyNoticeOnFile;
      $[90] = task.files;
      $[91] = fullRegistrationReviewReasons;
      $[92] = t10;
    } else {
      fullRegistrationReviewReasons = $[91];
      t10 = $[92];
    }
    let t11;
    if ($[95] !== t10) {
      t11 = [t10].filter(_temp12);
      $[95] = t10;
      $[96] = t11;
    } else {
      t11 = $[96];
    }
    const demographicsReviewReasons = t11;
    const ensurePatientStatus = taskStepStatus(taskSteps, [StepKey.ENSURE_PATIENT]);
    const careTeamStatus = taskStepStatus(taskSteps, [StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM]);
    const fullRegistrationStatus = ensurePatientStatus === "failed" ? "not_run" : ensurePatientStatus === "waiting" ? "waiting" : fullRegistrationReviewReasons.length > 0 ? "needs_action" : "automated";
    const demographicsStatus = ensurePatientStatus === "failed" ? "not_run" : careTeamStatus !== "automated" ? careTeamStatus : demographicsReviewReasons.length > 0 ? "needs_action" : "automated";
    const dispositionStatus = taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION]);
    const unevaluatedInsuranceDecision = followThroughEligible ? "Pending" : "Not evaluated";
    const inNetworkDisplay = !insuranceRequirements ? unevaluatedInsuranceDecision : insuranceRequirements.clinicNetwork === "out_of_network" || insuranceRequirements.providerNetwork === "out_of_network" ? "No" : insuranceRequirements.clinicNetwork === "in_network" && insuranceRequirements.providerNetwork === "in_network" ? "Yes" : insuranceRequirements.clinicNetwork === "not_applicable" && insuranceRequirements.providerNetwork === "not_applicable" ? "Not applicable" : "Manual review";
    let t12;
    if ($[97] !== insuranceRequirements || $[98] !== unevaluatedInsuranceDecision) {
      t12 = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement) : unevaluatedInsuranceDecision;
      $[97] = insuranceRequirements;
      $[98] = unevaluatedInsuranceDecision;
      $[99] = t12;
    } else {
      t12 = $[99];
    }
    const referralRequirementDisplay = t12;
    let t13;
    if ($[100] !== insuranceRequirements || $[101] !== unevaluatedInsuranceDecision) {
      t13 = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement) : unevaluatedInsuranceDecision;
      $[100] = insuranceRequirements;
      $[101] = unevaluatedInsuranceDecision;
      $[102] = t13;
    } else {
      t13 = $[102];
    }
    const authorizationRequirementDisplay = t13;
    let t14;
    if ($[103] !== opsCrossCheck.primaryPlanLabel) {
      t14 = {
        label: "Valerie plan",
        value: opsCrossCheck.primaryPlanLabel
      };
      $[103] = opsCrossCheck.primaryPlanLabel;
      $[104] = t14;
    } else {
      t14 = $[104];
    }
    let t15;
    if ($[105] !== opsCrossCheck.primarySubscriberId) {
      t15 = {
        label: "Valerie subscriber ID",
        value: opsCrossCheck.primarySubscriberId
      };
      $[105] = opsCrossCheck.primarySubscriberId;
      $[106] = t15;
    } else {
      t15 = $[106];
    }
    let t16;
    if ($[107] !== inNetworkDisplay) {
      t16 = {
        label: "In network",
        value: inNetworkDisplay
      };
      $[107] = inNetworkDisplay;
      $[108] = t16;
    } else {
      t16 = $[108];
    }
    let t17;
    if ($[109] !== referralRequirementDisplay) {
      t17 = {
        label: "Referral",
        value: referralRequirementDisplay
      };
      $[109] = referralRequirementDisplay;
      $[110] = t17;
    } else {
      t17 = $[110];
    }
    let t18;
    if ($[111] !== authorizationRequirementDisplay) {
      t18 = {
        label: "Authorization",
        value: authorizationRequirementDisplay
      };
      $[111] = authorizationRequirementDisplay;
      $[112] = t18;
    } else {
      t18 = $[112];
    }
    let t19;
    if ($[113] !== t14 || $[114] !== t15 || $[115] !== t16 || $[116] !== t17 || $[117] !== t18) {
      t19 = [t14, t15, t16, t17, t18];
      $[113] = t14;
      $[114] = t15;
      $[115] = t16;
      $[116] = t17;
      $[117] = t18;
      $[118] = t19;
    } else {
      t19 = $[118];
    }
    const insuranceCrossCheckItems = t19;
    const insuranceIntakeDetails = coverageApproval?.metadata ? <div className="space-y-3 rounded-md border p-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "Expected",
        value: coverageApproval.metadata.expectedCoverageCount
      }, {
        label: "In Athena",
        value: coverageApproval.metadata.currentCoverages.length
      }]} /><AthenaCoverageEvidence metadata={coverageApproval.metadata} /></div> : coverageReadback ? <div className="space-y-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "In Athena",
        value: coverageReadback.coverages.length
      }, {
        label: "Self pay",
        value: coverageReadback.primary.selfPay ? "Yes" : "No"
      }]} /><div className="space-y-1"><p className="font-medium">Currently in Athena</p>{coverageReadback.coverages.length === 0 ? <p>No coverage found.</p> : coverageReadback.coverages.map(_temp13)}</div></div> : followThroughEligible || assignmentStatus === "automated" ? <DecisionMetadata items={insuranceCrossCheckItems} /> : null;
    const t20 = `${opsCrossCheck.patientName} (DOB: ${opsCrossCheck.patientDateOfBirth})`;
    let t21;
    if ($[119] !== t20) {
      t21 = {
        label: "Patient",
        value: <CopyableContent text={t20} textClassName="text-xs" />
      };
      $[119] = t20;
      $[120] = t21;
    } else {
      t21 = $[120];
    }
    let t22;
    if ($[121] !== patientEhrId) {
      t22 = {
        label: "EHR ID",
        value: <CopyableContent text={patientEhrId} textClassName="text-xs" />
      };
      $[121] = patientEhrId;
      $[122] = t22;
    } else {
      t22 = $[122];
    }
    let t23;
    if ($[123] !== documentId) {
      t23 = {
        label: "Document ID",
        value: <CopyableContent text={documentId} textClassName="text-xs" />
      };
      $[123] = documentId;
      $[124] = t23;
    } else {
      t23 = $[124];
    }
    let t24;
    if ($[125] !== opsCrossCheck.patientPhoneNumber) {
      t24 = {
        label: "Phone",
        value: <CopyableContent text={opsCrossCheck.patientPhoneNumber} textClassName="text-xs" />
      };
      $[125] = opsCrossCheck.patientPhoneNumber;
      $[126] = t24;
    } else {
      t24 = $[126];
    }
    let t25;
    if ($[127] !== opsCrossCheck.patientFaxNumber) {
      t25 = {
        label: "Fax",
        value: <CopyableContent text={opsCrossCheck.patientFaxNumber} textClassName="text-xs" />
      };
      $[127] = opsCrossCheck.patientFaxNumber;
      $[128] = t25;
    } else {
      t25 = $[128];
    }
    let t26;
    if ($[129] !== opsCrossCheck.patientEthnicity) {
      t26 = {
        label: "Ethnicity",
        value: opsCrossCheck.patientEthnicity
      };
      $[129] = opsCrossCheck.patientEthnicity;
      $[130] = t26;
    } else {
      t26 = $[130];
    }
    let t27;
    if ($[131] !== opsCrossCheck.patientRace) {
      t27 = {
        label: "Race",
        value: opsCrossCheck.patientRace
      };
      $[131] = opsCrossCheck.patientRace;
      $[132] = t27;
    } else {
      t27 = $[132];
    }
    let t28;
    if ($[133] !== opsCrossCheck.patientLanguage) {
      t28 = {
        label: "Language",
        value: opsCrossCheck.patientLanguage
      };
      $[133] = opsCrossCheck.patientLanguage;
      $[134] = t28;
    } else {
      t28 = $[134];
    }
    let t29;
    if ($[135] !== t21 || $[136] !== t22 || $[137] !== t23 || $[138] !== t24 || $[139] !== t25 || $[140] !== t26 || $[141] !== t27 || $[142] !== t28) {
      t29 = <DecisionMetadata items={[t21, t22, t23, t24, t25, t26, t27, t28]} />;
      $[135] = t21;
      $[136] = t22;
      $[137] = t23;
      $[138] = t24;
      $[139] = t25;
      $[140] = t26;
      $[141] = t27;
      $[142] = t28;
      $[143] = t29;
    } else {
      t29 = $[143];
    }
    let t30;
    if ($[144] !== patientDisplayId) {
      t30 = patientDisplayId ? <Link href={`/patients/${patientDisplayId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">Open patient details<SquareArrowOutUpRight className="size-3.5" /></Link> : null;
      $[144] = patientDisplayId;
      $[145] = t30;
    } else {
      t30 = $[145];
    }
    let t31;
    if ($[146] !== t29 || $[147] !== t30) {
      t31 = <div className="space-y-3">{t29}{t30}</div>;
      $[146] = t29;
      $[147] = t30;
      $[148] = t31;
    } else {
      t31 = $[148];
    }
    let t32;
    if ($[149] !== patientLabel) {
      t32 = {
        label: "Patient type",
        value: patientLabel
      };
      $[149] = patientLabel;
      $[150] = t32;
    } else {
      t32 = $[150];
    }
    const t33 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
    let t34;
    if ($[151] !== t33) {
      t34 = {
        label: "Provider",
        value: <CopyableContent text={t33} textClassName="text-xs" />
      };
      $[151] = t33;
      $[152] = t34;
    } else {
      t34 = $[152];
    }
    const t35 = resolvedReceiving.providerNpi || NOT_AVAILABLE;
    let t36;
    if ($[153] !== t35) {
      t36 = {
        label: "NPI",
        value: <CopyableContent text={t35} textClassName="text-xs" />
      };
      $[153] = t35;
      $[154] = t36;
    } else {
      t36 = $[154];
    }
    const t37 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
    let t38;
    if ($[155] !== t37) {
      t38 = {
        label: "Location",
        value: <CopyableContent text={t37} textClassName="text-xs" />
      };
      $[155] = t37;
      $[156] = t38;
    } else {
      t38 = $[156];
    }
    const t39 = usesRetiredUsualProviderRule ? "Verify appointment history" : assignmentRequiresManualReview ? "Manual review" : "Confirmed";
    let t40;
    if ($[157] !== t39) {
      t40 = {
        label: "Resolution",
        value: t39
      };
      $[157] = t39;
      $[158] = t40;
    } else {
      t40 = $[158];
    }
    let t41;
    if ($[159] !== t32 || $[160] !== t34 || $[161] !== t36 || $[162] !== t38 || $[163] !== t40) {
      t41 = <DecisionMetadata items={[t32, t34, t36, t38, t40]} />;
      $[159] = t32;
      $[160] = t34;
      $[161] = t36;
      $[162] = t38;
      $[163] = t40;
      $[164] = t41;
    } else {
      t41 = $[164];
    }
    const t42 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
    const t43 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
    let t44;
    if ($[165] !== opsCrossCheck.diagnosis || $[166] !== opsCrossCheck.receivingProviderName || $[167] !== opsCrossCheck.receivingProviderNpi || $[168] !== providerSelectionEvidence || $[169] !== t42 || $[170] !== t43 || $[171] !== upcomingAppointmentProviderSummary) {
      t44 = <ProviderSelectionChecklist evidence={providerSelectionEvidence} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} referralProviderName={opsCrossCheck.receivingProviderName} referralProviderNpi={opsCrossCheck.receivingProviderNpi} diagnosis={opsCrossCheck.diagnosis} selectedProviderName={t42} selectedLocation={t43} />;
      $[165] = opsCrossCheck.diagnosis;
      $[166] = opsCrossCheck.receivingProviderName;
      $[167] = opsCrossCheck.receivingProviderNpi;
      $[168] = providerSelectionEvidence;
      $[169] = t42;
      $[170] = t43;
      $[171] = upcomingAppointmentProviderSummary;
      $[172] = t44;
    } else {
      t44 = $[172];
    }
    let t45;
    if ($[173] !== resolvedReceiving.advisories) {
      t45 = resolvedReceiving.advisories.length > 0 ? <div className="space-y-1 rounded-md border border-warn/30 bg-warn/5 p-3"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.advisories.map(_temp14)}</ul></div> : null;
      $[173] = resolvedReceiving.advisories;
      $[174] = t45;
    } else {
      t45 = $[174];
    }
    const t46 = assignmentConfirmationStatus === "needs_action" && resolvedReceiving.flags.length > 0 ? <><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.flags.map(_temp15)}</ul>{unresolvedUpcomingProviders.length > 0 ? <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/30 bg-warn/5 p-3"><span className="text-xs font-medium text-muted-foreground">Upcoming appointment provider</span>{unresolvedUpcomingProviders.map(_temp16)}</div> : null}</> : null;
    let t47;
    if ($[175] !== t41 || $[176] !== t44 || $[177] !== t45 || $[178] !== t46) {
      t47 = <div className="space-y-3">{t41}{t44}{t45}{t46}</div>;
      $[175] = t41;
      $[176] = t44;
      $[177] = t45;
      $[178] = t46;
      $[179] = t47;
    } else {
      t47 = $[179];
    }
    let t48;
    if ($[180] === Symbol.for("react.memo_cache_sentinel")) {
      t48 = <p className="text-sm font-medium">Patient Details</p>;
      $[180] = t48;
    } else {
      t48 = $[180];
    }
    let t49;
    if ($[181] !== opsCrossCheck.patientName) {
      t49 = {
        label: "Patient",
        value: opsCrossCheck.patientName
      };
      $[181] = opsCrossCheck.patientName;
      $[182] = t49;
    } else {
      t49 = $[182];
    }
    let t50;
    if ($[183] !== opsCrossCheck.patientDateOfBirth) {
      t50 = {
        label: "DOB",
        value: opsCrossCheck.patientDateOfBirth
      };
      $[183] = opsCrossCheck.patientDateOfBirth;
      $[184] = t50;
    } else {
      t50 = $[184];
    }
    let t51;
    if ($[185] !== opsCrossCheck.patientPhoneNumber) {
      t51 = {
        label: "Phone",
        value: opsCrossCheck.patientPhoneNumber
      };
      $[185] = opsCrossCheck.patientPhoneNumber;
      $[186] = t51;
    } else {
      t51 = $[186];
    }
    let t52;
    if ($[187] !== opsCrossCheck.patientHomeAddress) {
      t52 = {
        label: "Home address",
        value: opsCrossCheck.patientHomeAddress
      };
      $[187] = opsCrossCheck.patientHomeAddress;
      $[188] = t52;
    } else {
      t52 = $[188];
    }
    let t53;
    if ($[189] !== t49 || $[190] !== t50 || $[191] !== t51 || $[192] !== t52) {
      t53 = <div className="space-y-2 rounded-md border p-3">{t48}<DecisionMetadata items={[t49, t50, t51, t52]} /></div>;
      $[189] = t49;
      $[190] = t50;
      $[191] = t51;
      $[192] = t52;
      $[193] = t53;
    } else {
      t53 = $[193];
    }
    let t54;
    if ($[194] === Symbol.for("react.memo_cache_sentinel")) {
      t54 = <p className="text-sm font-medium">Care Team</p>;
      $[194] = t54;
    } else {
      t54 = $[194];
    }
    let t55;
    if ($[195] === Symbol.for("react.memo_cache_sentinel")) {
      t55 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Usual Provider</p>;
      $[195] = t55;
    } else {
      t55 = $[195];
    }
    let t56;
    if ($[196] !== resolvedReceiving.providerLabel || $[197] !== resolvedReceiving.providerNpi || $[198] !== resolvedReceiving.usualProviderWriteSafe) {
      t56 = <div className="space-y-1">{t55}<DecisionMetadata items={resolvedReceiving.usualProviderWriteSafe ? [{
          label: "Provider",
          value: <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "NPI",
          value: <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "Action",
          value: "Update (new patient)"
        }] : [{
          label: "Action",
          value: "Leave unchanged (established patient)"
        }]} /></div>;
      $[196] = resolvedReceiving.providerLabel;
      $[197] = resolvedReceiving.providerNpi;
      $[198] = resolvedReceiving.usualProviderWriteSafe;
      $[199] = t56;
    } else {
      t56 = $[199];
    }
    let t57;
    if ($[200] === Symbol.for("react.memo_cache_sentinel")) {
      t57 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referring Provider</p>;
      $[200] = t57;
    } else {
      t57 = $[200];
    }
    let t58;
    if ($[201] !== opsCrossCheck.referringProviderName) {
      t58 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
      };
      $[201] = opsCrossCheck.referringProviderName;
      $[202] = t58;
    } else {
      t58 = $[202];
    }
    let t59;
    if ($[203] !== opsCrossCheck.referringProviderNpi) {
      t59 = {
        label: "NPI",
        value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
      };
      $[203] = opsCrossCheck.referringProviderNpi;
      $[204] = t59;
    } else {
      t59 = $[204];
    }
    let t60;
    if ($[205] !== opsCrossCheck.referringLocationAddress) {
      t60 = {
        label: "Address",
        value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
      };
      $[205] = opsCrossCheck.referringLocationAddress;
      $[206] = t60;
    } else {
      t60 = $[206];
    }
    let t61;
    if ($[207] !== opsCrossCheck.referringProviderSpecialty) {
      t61 = {
        label: "Specialty",
        value: opsCrossCheck.referringProviderSpecialty
      };
      $[207] = opsCrossCheck.referringProviderSpecialty;
      $[208] = t61;
    } else {
      t61 = $[208];
    }
    let t62;
    if ($[209] !== t58 || $[210] !== t59 || $[211] !== t60 || $[212] !== t61) {
      t62 = <div className="space-y-1">{t57}<DecisionMetadata items={[t58, t59, t60, t61]} /></div>;
      $[209] = t58;
      $[210] = t59;
      $[211] = t60;
      $[212] = t61;
      $[213] = t62;
    } else {
      t62 = $[213];
    }
    let t63;
    if ($[214] !== isPrimaryCareProvider || $[215] !== opsCrossCheck.referringProviderName || $[216] !== opsCrossCheck.referringProviderNpi || $[217] !== primaryCareProviderAlreadyPresent) {
      t63 = isPrimaryCareProvider && !primaryCareProviderAlreadyPresent ? <div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">PCP</p><DecisionMetadata items={[{
          label: "Name",
          value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
        }, {
          label: "NPI",
          value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
        }]} /></div> : null;
      $[214] = isPrimaryCareProvider;
      $[215] = opsCrossCheck.referringProviderName;
      $[216] = opsCrossCheck.referringProviderNpi;
      $[217] = primaryCareProviderAlreadyPresent;
      $[218] = t63;
    } else {
      t63 = $[218];
    }
    let t64;
    if ($[219] !== t56 || $[220] !== t62 || $[221] !== t63) {
      t64 = <div className="space-y-3 rounded-md border p-3">{t54}{t56}{t62}{t63}</div>;
      $[219] = t56;
      $[220] = t62;
      $[221] = t63;
      $[222] = t64;
    } else {
      t64 = $[222];
    }
    let t65;
    if ($[223] === Symbol.for("react.memo_cache_sentinel")) {
      t65 = <p className="text-sm font-medium">Emergency Contact</p>;
      $[223] = t65;
    } else {
      t65 = $[223];
    }
    let t66;
    if ($[224] !== opsCrossCheck.emergencyContactName) {
      t66 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.emergencyContactName} textClassName="text-xs" />
      };
      $[224] = opsCrossCheck.emergencyContactName;
      $[225] = t66;
    } else {
      t66 = $[225];
    }
    let t67;
    if ($[226] !== opsCrossCheck.emergencyContactRelationship) {
      t67 = {
        label: "Relationship",
        value: <CopyableContent text={opsCrossCheck.emergencyContactRelationship} textClassName="text-xs" />
      };
      $[226] = opsCrossCheck.emergencyContactRelationship;
      $[227] = t67;
    } else {
      t67 = $[227];
    }
    let t68;
    if ($[228] !== opsCrossCheck.emergencyContactPhoneNumber) {
      t68 = {
        label: "Phone",
        value: <CopyableContent text={opsCrossCheck.emergencyContactPhoneNumber} textClassName="text-xs" />
      };
      $[228] = opsCrossCheck.emergencyContactPhoneNumber;
      $[229] = t68;
    } else {
      t68 = $[229];
    }
    let t69;
    if ($[230] !== t66 || $[231] !== t67 || $[232] !== t68) {
      t69 = <div className="space-y-2 rounded-md border p-3">{t65}<DecisionMetadata items={[t66, t67, t68]} /></div>;
      $[230] = t66;
      $[231] = t67;
      $[232] = t68;
      $[233] = t69;
    } else {
      t69 = $[233];
    }
    let t70;
    if ($[234] !== demographicsReviewReasons) {
      t70 = demographicsReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{demographicsReviewReasons.map(_temp17)}</ul> : null;
      $[234] = demographicsReviewReasons;
      $[235] = t70;
    } else {
      t70 = $[235];
    }
    let t71;
    if ($[236] !== t53 || $[237] !== t64 || $[238] !== t69 || $[239] !== t70) {
      t71 = <div className="space-y-3">{t53}{t64}{t69}{t70}</div>;
      $[236] = t53;
      $[237] = t64;
      $[238] = t69;
      $[239] = t70;
      $[240] = t71;
    } else {
      t71 = $[240];
    }
    let t72;
    if ($[241] !== opsCrossCheck.primaryPlanLabel) {
      t72 = <CopyableContent text={opsCrossCheck.primaryPlanLabel} textClassName="text-xs" />;
      $[241] = opsCrossCheck.primaryPlanLabel;
      $[242] = t72;
    } else {
      t72 = $[242];
    }
    let t73;
    if ($[243] !== opsCrossCheck.isSpecialCaseInsurance) {
      t73 = opsCrossCheck.isSpecialCaseInsurance ? <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn-foreground">Special Case Insurance</Badge> : null;
      $[243] = opsCrossCheck.isSpecialCaseInsurance;
      $[244] = t73;
    } else {
      t73 = $[244];
    }
    let t74;
    if ($[245] !== t72 || $[246] !== t73) {
      t74 = {
        label: "Plan (from Valerie)",
        value: <span className="inline-flex items-center gap-1.5">{t72}{t73}</span>
      };
      $[245] = t72;
      $[246] = t73;
      $[247] = t74;
    } else {
      t74 = $[247];
    }
    let t75;
    if ($[248] !== opsCrossCheck.primarySubscriberId) {
      t75 = {
        label: "Subscriber ID (from Valerie)",
        value: <CopyableContent text={opsCrossCheck.primarySubscriberId} textClassName="text-xs" />
      };
      $[248] = opsCrossCheck.primarySubscriberId;
      $[249] = t75;
    } else {
      t75 = $[249];
    }
    const t76 = coverageReadbackComplete ? "Yes" : "No";
    let t77;
    if ($[250] !== t76) {
      t77 = {
        label: "Synced to Athena",
        value: t76
      };
      $[250] = t76;
      $[251] = t77;
    } else {
      t77 = $[251];
    }
    const t78 = coverageReadback?.primary.selfPay ? "N/A \u2014 Self Pay" : inNetworkDisplay;
    let t79;
    if ($[252] !== t78) {
      t79 = {
        label: "In network",
        value: t78
      };
      $[252] = t78;
      $[253] = t79;
    } else {
      t79 = $[253];
    }
    let t80;
    if ($[254] !== t74 || $[255] !== t75 || $[256] !== t77 || $[257] !== t79) {
      t80 = <DecisionMetadata items={[t74, t75, t77, t79]} />;
      $[254] = t74;
      $[255] = t75;
      $[256] = t77;
      $[257] = t79;
      $[258] = t80;
    } else {
      t80 = $[258];
    }
    let t81;
    if ($[259] === Symbol.for("react.memo_cache_sentinel")) {
      t81 = <summary className="cursor-pointer text-muted-foreground">More insurance detail</summary>;
      $[259] = t81;
    } else {
      t81 = $[259];
    }
    let t82;
    if ($[260] === Symbol.for("react.memo_cache_sentinel")) {
      t82 = <p className="text-sm font-medium">Coverage</p>;
      $[260] = t82;
    } else {
      t82 = $[260];
    }
    const t83 = coverageReadback?.primary.selfPay ? "Yes" : "No";
    let t84;
    if ($[261] !== t83) {
      t84 = <DecisionMetadata items={[{
        label: "Self pay",
        value: t83
      }]} />;
      $[261] = t83;
      $[262] = t84;
    } else {
      t84 = $[262];
    }
    let t85;
    if ($[263] !== referralId) {
      t85 = <TprStediEligibilityValues referralId={referralId} />;
      $[263] = referralId;
      $[264] = t85;
    } else {
      t85 = $[264];
    }
    let t86;
    if ($[265] !== insuranceIntakeDetails) {
      t86 = insuranceIntakeDetails ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">Show Athena coverage detail</summary><div className="mt-2">{insuranceIntakeDetails}</div></details> : null;
      $[265] = insuranceIntakeDetails;
      $[266] = t86;
    } else {
      t86 = $[266];
    }
    let t87;
    if ($[267] !== t84 || $[268] !== t85 || $[269] !== t86) {
      t87 = <div className="space-y-2 rounded-md border p-3">{t82}{t84}{t85}{t86}</div>;
      $[267] = t84;
      $[268] = t85;
      $[269] = t86;
      $[270] = t87;
    } else {
      t87 = $[270];
    }
    let t88;
    if ($[271] === Symbol.for("react.memo_cache_sentinel")) {
      t88 = <p className="text-sm font-medium">Athena Eligibility</p>;
      $[271] = t88;
    } else {
      t88 = $[271];
    }
    let t89;
    if ($[272] !== followThroughEligible || $[273] !== insuranceStatusDisplay || $[274] !== skippedFollowThroughSummary) {
      t89 = <div className="space-y-2 rounded-md border p-3">{t88}{followThroughEligible ? <DecisionMetadata items={[{
          label: "Athena eligibility",
          value: insuranceStatusDisplay
        }]} /> : <p className="text-sm text-muted-foreground">{skippedFollowThroughSummary}</p>}</div>;
      $[272] = followThroughEligible;
      $[273] = insuranceStatusDisplay;
      $[274] = skippedFollowThroughSummary;
      $[275] = t89;
    } else {
      t89 = $[275];
    }
    let t90;
    if ($[276] !== insuranceRequirements) {
      t90 = insuranceRequirements?.reviewReasons.length ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Network Review Reasons</p><ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">{insuranceRequirements.reviewReasons.map(_temp18)}</ul></div> : null;
      $[276] = insuranceRequirements;
      $[277] = t90;
    } else {
      t90 = $[277];
    }
    let t91;
    if ($[278] !== t87 || $[279] !== t89 || $[280] !== t90) {
      t91 = <details className="text-xs">{t81}<div className="mt-3 space-y-3">{t87}{t89}{t90}</div></details>;
      $[278] = t87;
      $[279] = t89;
      $[280] = t90;
      $[281] = t91;
    } else {
      t91 = $[281];
    }
    let t92;
    if ($[282] !== t80 || $[283] !== t91) {
      t92 = <div className="space-y-3">{t80}{t91}</div>;
      $[282] = t80;
      $[283] = t91;
      $[284] = t92;
    } else {
      t92 = $[284];
    }
    const t93 = resolveConsolidatedStatus([pcpFaxStatus, authorizationStatus]);
    let t94;
    if ($[285] !== followThroughEligible || $[286] !== opsApproval?.metadata?.instruction || $[287] !== opsKind || $[288] !== referralAuthorization || $[289] !== skippedFollowThroughSummary) {
      t94 = opsKind === "pcp_referral_fax" ? opsApproval?.metadata?.instruction ?? "This plan requires the referral to come from the PCP. Export the chart and send the outbound fax." : opsKind === "authorization_review" ? opsApproval?.metadata?.instruction ?? "Review the Athena referral authorization object, then continue." : opsKind === "manual_authorization" ? opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION : referralAuthorization?.outcome === "created" ? `${referralAuthorization.write === "authorization" ? "Authorization" : "Paper referral"} object ${referralAuthorization.alreadyExisted ? "already existed" : "was created"} in Athena${referralAuthorization.pending ? " with number pending" : ""}.` : referralAuthorization?.outcome === "needs_pending_request" ? "No authorization number is on file. An outbound request letter is required next." : tprReferralAuthNeedsReview(referralAuthorization) ? "No Athena object was created because the authorization requirements need manual review." : referralAuthorization?.outcome === "not_needed" ? `No Athena object was needed (${formatFromUnderscoreToHumanReadable(referralAuthorization.reason)}).` : followThroughEligible ? "Waiting for network, authorization, and referral-requirement decisions." : skippedFollowThroughSummary;
      $[285] = followThroughEligible;
      $[286] = opsApproval?.metadata?.instruction;
      $[287] = opsKind;
      $[288] = referralAuthorization;
      $[289] = skippedFollowThroughSummary;
      $[290] = t94;
    } else {
      t94 = $[290];
    }
    let t95;
    if ($[291] === Symbol.for("react.memo_cache_sentinel")) {
      t95 = <p className="text-sm font-medium">PCP Referral Fax</p>;
      $[291] = t95;
    } else {
      t95 = $[291];
    }
    let t96;
    if ($[292] !== followThroughEligible || $[293] !== insuranceRequirements || $[294] !== isPrimaryCareProvider || $[295] !== skippedFollowThroughSummary) {
      t96 = <div className="space-y-2 rounded-md border p-3">{t95}{insuranceRequirements ? <DecisionMetadata items={[{
          label: "PCP referral",
          value: insuranceRequirements.referralMustBeFromPcp ? "Required" : "No"
        }, {
          label: "Referring is PCP",
          value: isPrimaryCareProvider ? "Yes" : "No"
        }]} /> : <p className="text-sm text-muted-foreground">{followThroughEligible ? "Waiting for network/requirements decision." : skippedFollowThroughSummary}</p>}</div>;
      $[292] = followThroughEligible;
      $[293] = insuranceRequirements;
      $[294] = isPrimaryCareProvider;
      $[295] = skippedFollowThroughSummary;
      $[296] = t96;
    } else {
      t96 = $[296];
    }
    let t97;
    if ($[297] === Symbol.for("react.memo_cache_sentinel")) {
      t97 = <p className="text-sm font-medium">Referral Authorization</p>;
      $[297] = t97;
    } else {
      t97 = $[297];
    }
    let t98;
    if ($[298] !== insuranceRequirements || $[299] !== referralAuthorization) {
      t98 = insuranceRequirements ? <DecisionMetadata items={[{
        label: "Authorization",
        value: formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement)
      }, {
        label: "Referral",
        value: formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement)
      }, {
        label: "Outcome",
        value: referralAuthorization ? formatFromUnderscoreToHumanReadable(referralAuthorization.outcome) : "Pending"
      }, ...(referralAuthorization?.outcome === "created" ? [{
        label: "Athena object",
        value: <CopyableContent text={referralAuthorization.referralAuthId} textClassName="text-xs" />
      }, {
        label: "Appointment",
        value: referralAuthorization.needsManualAppointmentAttach ? "Attach manually" : referralAuthorization.appointmentAttached ? "Attached" : "None in window"
      }] : [])]} /> : null;
      $[298] = insuranceRequirements;
      $[299] = referralAuthorization;
      $[300] = t98;
    } else {
      t98 = $[300];
    }
    let t99;
    if ($[301] === Symbol.for("react.memo_cache_sentinel")) {
      t99 = <p className="text-sm font-medium">On the Athena chart</p>;
      $[301] = t99;
    } else {
      t99 = $[301];
    }
    let t100;
    if ($[302] === Symbol.for("react.memo_cache_sentinel")) {
      t100 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referral authorizations</p>;
      $[302] = t100;
    } else {
      t100 = $[302];
    }
    let t101;
    if ($[303] !== chartReferralAuths) {
      t101 = <div className="space-y-1">{t100}{chartReferralAuths.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{chartReferralAuths.map(_temp19)}</ul> : <p className="text-sm text-muted-foreground">None on this insurance.</p>}</div>;
      $[303] = chartReferralAuths;
      $[304] = t101;
    } else {
      t101 = $[304];
    }
    let t102;
    if ($[305] === Symbol.for("react.memo_cache_sentinel")) {
      t102 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request letters</p>;
      $[305] = t102;
    } else {
      t102 = $[305];
    }
    let t103;
    if ($[306] !== chartRequestLetters) {
      t103 = <div className="space-y-1">{t102}{chartRequestLetters.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{chartRequestLetters.map(_temp20)}</ul> : <p className="text-sm text-muted-foreground">None uploaded on this task.</p>}</div>;
      $[306] = chartRequestLetters;
      $[307] = t103;
    } else {
      t103 = $[307];
    }
    let t104;
    if ($[308] !== t101 || $[309] !== t103) {
      t104 = <div className="space-y-2">{t99}{t101}{t103}</div>;
      $[308] = t101;
      $[309] = t103;
      $[310] = t104;
    } else {
      t104 = $[310];
    }
    const t105 = Boolean(task.referral && patientEhrId !== NOT_AVAILABLE);
    let t106;
    if ($[311] !== referralAuthNeedsPendingUpload || $[312] !== t105 || $[313] !== task.id) {
      t106 = <TprSyncAuthButtons canUploadLetter={referralAuthNeedsPendingUpload} enabled={t105} taskId={task.id} />;
      $[311] = referralAuthNeedsPendingUpload;
      $[312] = t105;
      $[313] = task.id;
      $[314] = t106;
    } else {
      t106 = $[314];
    }
    let t107;
    if ($[315] === Symbol.for("react.memo_cache_sentinel")) {
      t107 = <summary className="cursor-pointer text-muted-foreground">How to complete this manually</summary>;
      $[315] = t107;
    } else {
      t107 = $[315];
    }
    let t108;
    if ($[316] === Symbol.for("react.memo_cache_sentinel")) {
      t108 = <p className="font-medium text-foreground">Referral Required</p>;
      $[316] = t108;
    } else {
      t108 = $[316];
    }
    let t109;
    if ($[317] === Symbol.for("react.memo_cache_sentinel")) {
      t109 = <li>If a referral is required, confirm the received referral automatically created an object under the Authorization section.</li>;
      $[317] = t109;
    } else {
      t109 = $[317];
    }
    let t110;
    let t111;
    if ($[318] === Symbol.for("react.memo_cache_sentinel")) {
      t110 = <div>{t108}<ul className="list-disc space-y-1 pl-5 text-muted-foreground">{t109}<li>If it did not, manually create the referral object with:<ul className="list-disc space-y-1 pl-5"><li>Insurance / authorization number: always "paper"</li><li>Referral dates — default (if not provided): today through 1 year later</li><li>Number of visits — default (if not provided): 12 visits</li><li>ICD-10 codes</li><li>Referring provider</li><li>Specialty</li><li>Only if listed on the referral doc: receiving provider and department</li></ul></li></ul></div>;
      t111 = <p className="font-medium text-foreground">Referral Authorization Required</p>;
      $[318] = t110;
      $[319] = t111;
    } else {
      t110 = $[318];
      t111 = $[319];
    }
    let t112;
    if ($[320] === Symbol.for("react.memo_cache_sentinel")) {
      t112 = <li>Same workflow as above, but enter the authorization number in the Insurance / authorization number field.</li>;
      $[320] = t112;
    } else {
      t112 = $[320];
    }
    let t113;
    if ($[321] === Symbol.for("react.memo_cache_sentinel")) {
      t113 = <details className="text-xs">{t107}<div className="mt-2 space-y-3">{t110}<div>{t111}<ul className="list-disc space-y-1 pl-5 text-muted-foreground">{t112}<li>Only send a fax if there is no authorization number. When sending an authorization request for a missing auth number:<ul className="list-disc space-y-1 pl-5"><li>Manually export the doc to the referring provider via AthenaFax.</li><li>Add an authorization object with "Pending" in the auth number field.</li><li>In all notes (patient notes, document internal note, action note): "Referral Authorization Requested [Date]"</li></ul></li></ul></div></div></details>;
      $[321] = t113;
    } else {
      t113 = $[321];
    }
    let t114;
    if ($[322] !== t104 || $[323] !== t106 || $[324] !== t98) {
      t114 = <div className="space-y-2 rounded-md border p-3">{t97}{t98}{t104}{t106}{t113}</div>;
      $[322] = t104;
      $[323] = t106;
      $[324] = t98;
      $[325] = t114;
    } else {
      t114 = $[325];
    }
    let t115;
    if ($[326] !== t114 || $[327] !== t96) {
      t115 = <div className="space-y-3">{t96}{t114}</div>;
      $[326] = t114;
      $[327] = t96;
      $[328] = t115;
    } else {
      t115 = $[328];
    }
    let t116;
    if ($[329] !== authRequestUploaded || $[330] !== referralAuthNeedsPendingUpload) {
      t116 = referralAuthNeedsPendingUpload || authRequestUploaded ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Authorization Request Letter</p><DecisionMetadata items={[{
          label: "Request letter",
          value: authRequestUploaded ? "Uploaded" : "Waiting for ops"
        }]} /><ol className="list-decimal space-y-1 pl-5 text-sm"><li>Export the patient chart in Athena. Do not skip this export.</li><li>Click Upload request letter. The letter is not uploaded automatically.</li><li>Athena then receives a Referral Auth object with number pending.</li></ol></div> : null;
      $[329] = authRequestUploaded;
      $[330] = referralAuthNeedsPendingUpload;
      $[331] = t116;
    } else {
      t116 = $[331];
    }
    let t117;
    if ($[332] !== hasMissingInformation || $[333] !== missingInfoUploadComplete) {
      t117 = hasMissingInformation ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Missing Patient Phone Packet</p><DecisionMetadata items={[{
          label: "Missing-info packet",
          value: missingInfoUploadComplete ? "Sent to Athena chart" : "Waiting for automation"
        }]} /><p className="text-sm text-muted-foreground">Generated automatically when the patient phone number is missing — no upload needed here. Once it appears in the Athena chart, send it from Athena like any other outbound fax.</p></div> : null;
      $[332] = hasMissingInformation;
      $[333] = missingInfoUploadComplete;
      $[334] = t117;
    } else {
      t117 = $[334];
    }
    const t118 = outboundFaxStatus === "not_needed" ? <p className="text-sm text-muted-foreground">Nothing to send — the referral authorization doesn't need an outbound request, and the patient phone number isn't missing.</p> : null;
    let t119;
    if ($[335] !== t116 || $[336] !== t117 || $[337] !== t118) {
      t119 = <div className="space-y-3">{t116}{t117}{t118}</div>;
      $[335] = t116;
      $[336] = t117;
      $[337] = t118;
      $[338] = t119;
    } else {
      t119 = $[338];
    }
    let t120;
    let t121;
    if ($[339] === Symbol.for("react.memo_cache_sentinel")) {
      t120 = {
        label: "Calls",
        value: "Set to Yes only if blank \u2014 existing value is not overridden"
      };
      t121 = {
        label: "Texts",
        value: "Set to Yes only if blank \u2014 existing value is not overridden"
      };
      $[339] = t120;
      $[340] = t121;
    } else {
      t120 = $[339];
      t121 = $[340];
    }
    const t122 = privacyNoticeOnFile === null ? "Verify selected" : privacyNoticeOnFile ? "Selected" : "Not selected";
    let t123;
    if ($[341] !== t122) {
      t123 = {
        label: "Privacy Notice",
        value: t122
      };
      $[341] = t122;
      $[342] = t123;
    } else {
      t123 = $[342];
    }
    let t124;
    if ($[343] === Symbol.for("react.memo_cache_sentinel")) {
      t124 = {
        label: "How did you hear about us",
        value: "Set to Physician Referral if blank"
      };
      $[343] = t124;
    } else {
      t124 = $[343];
    }
    let t125;
    if ($[344] !== t123) {
      t125 = <DecisionMetadata items={[t120, t121, t123, t124]} />;
      $[344] = t123;
      $[345] = t125;
    } else {
      t125 = $[345];
    }
    let t126;
    if ($[346] !== fullRegistrationReviewReasons) {
      t126 = fullRegistrationReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-warn-foreground">{fullRegistrationReviewReasons.map(_temp21)}</ul> : null;
      $[346] = fullRegistrationReviewReasons;
      $[347] = t126;
    } else {
      t126 = $[347];
    }
    let t127;
    if ($[348] !== t125 || $[349] !== t126) {
      t127 = <div className="space-y-3">{t125}{t126}</div>;
      $[348] = t125;
      $[349] = t126;
      $[350] = t127;
    } else {
      t127 = $[350];
    }
    let t128;
    if ($[351] !== documentId) {
      t128 = <DecisionMetadata items={[{
        label: "Document ID",
        value: <CopyableContent text={documentId} textClassName="text-xs" />
      }]} />;
      $[351] = documentId;
      $[352] = t128;
    } else {
      t128 = $[352];
    }
    let t129;
    if ($[353] === Symbol.for("react.memo_cache_sentinel")) {
      t129 = <p className="text-sm font-medium">Referral Fields — fill out in order</p>;
      $[353] = t129;
    } else {
      t129 = $[353];
    }
    let t130;
    if ($[354] === Symbol.for("react.memo_cache_sentinel")) {
      t130 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1. Referring Provider</p>;
      $[354] = t130;
    } else {
      t130 = $[354];
    }
    let t131;
    if ($[355] !== opsCrossCheck.referringProviderName) {
      t131 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
      };
      $[355] = opsCrossCheck.referringProviderName;
      $[356] = t131;
    } else {
      t131 = $[356];
    }
    let t132;
    if ($[357] !== opsCrossCheck.referringProviderNpi) {
      t132 = {
        label: "NPI",
        value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
      };
      $[357] = opsCrossCheck.referringProviderNpi;
      $[358] = t132;
    } else {
      t132 = $[358];
    }
    let t133;
    if ($[359] !== opsCrossCheck.referringLocationAddress) {
      t133 = {
        label: "Address",
        value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
      };
      $[359] = opsCrossCheck.referringLocationAddress;
      $[360] = t133;
    } else {
      t133 = $[360];
    }
    let t134;
    if ($[361] !== t131 || $[362] !== t132 || $[363] !== t133) {
      t134 = <div className="space-y-1">{t130}<DecisionMetadata items={[t131, t132, t133]} /></div>;
      $[361] = t131;
      $[362] = t132;
      $[363] = t133;
      $[364] = t134;
    } else {
      t134 = $[364];
    }
    let t135;
    if ($[365] === Symbol.for("react.memo_cache_sentinel")) {
      t135 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">2. New or Established Patient</p>;
      $[365] = t135;
    } else {
      t135 = $[365];
    }
    let t136;
    if ($[366] !== patientLabel) {
      t136 = <div className="space-y-1">{t135}<DecisionMetadata items={[{
          label: "Patient type",
          value: <CopyableContent text={patientLabel} textClassName="text-xs" />
        }]} /></div>;
      $[366] = patientLabel;
      $[367] = t136;
    } else {
      t136 = $[367];
    }
    let t137;
    if ($[368] === Symbol.for("react.memo_cache_sentinel")) {
      t137 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">3. Referral Date</p>;
      $[368] = t137;
    } else {
      t137 = $[368];
    }
    let t138;
    if ($[369] !== opsCrossCheck.referralDate) {
      t138 = <div className="space-y-1">{t137}<DecisionMetadata items={[{
          label: "Referral date",
          value: <CopyableContent text={opsCrossCheck.referralDate} textClassName="text-xs" />
        }]} /></div>;
      $[369] = opsCrossCheck.referralDate;
      $[370] = t138;
    } else {
      t138 = $[370];
    }
    let t139;
    if ($[371] === Symbol.for("react.memo_cache_sentinel")) {
      t139 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">4. Urgency</p>;
      $[371] = t139;
    } else {
      t139 = $[371];
    }
    const t140 = urgency.isUrgent ? "Yes" : "No";
    let t141;
    if ($[372] !== t140) {
      t141 = {
        label: "Urgent",
        value: t140
      };
      $[372] = t140;
      $[373] = t141;
    } else {
      t141 = $[373];
    }
    let t142;
    if ($[374] !== urgency.reason) {
      t142 = urgency.reason ? [{
        label: "Reason",
        value: urgency.reason
      }] : [];
      $[374] = urgency.reason;
      $[375] = t142;
    } else {
      t142 = $[375];
    }
    const t143 = urgency.schedulingPriority || NOT_AVAILABLE;
    let t144;
    if ($[376] !== t143) {
      t144 = {
        label: "Scheduling priority",
        value: t143
      };
      $[376] = t143;
      $[377] = t144;
    } else {
      t144 = $[377];
    }
    let t145;
    if ($[378] !== t141 || $[379] !== t142 || $[380] !== t144) {
      t145 = <div className="space-y-1">{t139}<DecisionMetadata items={[t141, ...t142, t144]} /></div>;
      $[378] = t141;
      $[379] = t142;
      $[380] = t144;
      $[381] = t145;
    } else {
      t145 = $[381];
    }
    let t146;
    if ($[382] === Symbol.for("react.memo_cache_sentinel")) {
      t146 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">5. Internal Note</p>;
      $[382] = t146;
    } else {
      t146 = $[382];
    }
    let t147;
    if ($[383] !== internalNoteToEnter) {
      t147 = <div className="space-y-1">{t146}<DecisionMetadata items={[{
          label: "Internal Note",
          value: <CopyableContent text={internalNoteToEnter} textClassName="text-xs" />
        }]} /></div>;
      $[383] = internalNoteToEnter;
      $[384] = t147;
    } else {
      t147 = $[384];
    }
    let t148;
    if ($[385] !== t134 || $[386] !== t136 || $[387] !== t138 || $[388] !== t145 || $[389] !== t147) {
      t148 = <div className="space-y-3 rounded-md border p-3">{t129}{t134}{t136}{t138}{t145}{t147}</div>;
      $[385] = t134;
      $[386] = t136;
      $[387] = t138;
      $[388] = t145;
      $[389] = t147;
      $[390] = t148;
    } else {
      t148 = $[390];
    }
    let t149;
    if ($[391] !== t128 || $[392] !== t148) {
      t149 = <div className="space-y-3">{t128}{t148}</div>;
      $[391] = t128;
      $[392] = t148;
      $[393] = t149;
    } else {
      t149 = $[393];
    }
    let t150;
    if ($[394] !== documentUploadLabels.length) {
      t150 = <DecisionMetadata items={[{
        label: "Documents",
        value: documentUploadLabels.length
      }]} />;
      $[394] = documentUploadLabels.length;
      $[395] = t150;
    } else {
      t150 = $[395];
    }
    let t151;
    if ($[396] === Symbol.for("react.memo_cache_sentinel")) {
      t151 = <p className="text-sm font-medium">Expected Athena uploads</p>;
      $[396] = t151;
    } else {
      t151 = $[396];
    }
    let t152;
    if ($[397] !== documentUploadLabels) {
      t152 = <div className="space-y-1">{t151}{documentUploadLabels.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{documentUploadLabels.map(_temp22)}</ul> : <p className="text-sm text-muted-foreground">No documents were selected for upload.</p>}</div>;
      $[397] = documentUploadLabels;
      $[398] = t152;
    } else {
      t152 = $[398];
    }
    let t153;
    if ($[399] !== t150 || $[400] !== t152) {
      t153 = <div className="space-y-3">{t150}{t152}</div>;
      $[399] = t150;
      $[400] = t152;
      $[401] = t153;
    } else {
      t153 = $[401];
    }
    let t154;
    if ($[402] === Symbol.for("react.memo_cache_sentinel")) {
      t154 = <p className="text-sm font-medium">Bucket</p>;
      $[402] = t154;
    } else {
      t154 = $[402];
    }
    const t155 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? NO_CONFIDENT_BUCKET_SUGGESTION : SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel;
    let t156;
    if ($[403] !== t155) {
      t156 = {
        label: "Suggested bucket",
        value: <CopyableContent text={t155} />
      };
      $[403] = t155;
      $[404] = t156;
    } else {
      t156 = $[404];
    }
    const t157 = hasMissingInformation ? "Required" : "No";
    let t158;
    if ($[405] !== t157) {
      t158 = {
        label: "Missing information",
        value: t157
      };
      $[405] = t157;
      $[406] = t158;
    } else {
      t158 = $[406];
    }
    const t159 = authRequestUploaded ? "Uploaded" : "Not required";
    let t160;
    if ($[407] !== t159) {
      t160 = {
        label: "Authorization request",
        value: t159
      };
      $[407] = t159;
      $[408] = t160;
    } else {
      t160 = $[408];
    }
    let t161;
    if ($[409] !== t156 || $[410] !== t158 || $[411] !== t160) {
      t161 = <DecisionMetadata items={[t156, t158, t160]} />;
      $[409] = t156;
      $[410] = t158;
      $[411] = t160;
      $[412] = t161;
    } else {
      t161 = $[412];
    }
    let t162;
    if ($[413] !== decisionTrace) {
      t162 = decisionTrace.length > 0 ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">How this was determined</summary><ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">{decisionTrace.map(_temp23)}</ul></details> : null;
      $[413] = decisionTrace;
      $[414] = t162;
    } else {
      t162 = $[414];
    }
    let t163;
    if ($[415] !== t161 || $[416] !== t162) {
      t163 = <div className="space-y-2 rounded-md border p-3">{t154}{t161}{t162}</div>;
      $[415] = t161;
      $[416] = t162;
      $[417] = t163;
    } else {
      t163 = $[417];
    }
    let t164;
    if ($[418] !== notesComplete || $[419] !== resolvedReceiving.routedToSscardio || $[420] !== task.id || $[421] !== task.referral || $[422] !== task.type || $[423] !== workflowStillProcessing) {
      t164 = !resolvedReceiving.routedToSscardio && !workflowStillProcessing && notesComplete && task.type === TaskType.REFERRAL && task.referral && <TaskSchedulingRecommendation taskId={task.id} referral={task.referral} preferAgenticReceivingProvider={true} />;
      $[418] = notesComplete;
      $[419] = resolvedReceiving.routedToSscardio;
      $[420] = task.id;
      $[421] = task.referral;
      $[422] = task.type;
      $[423] = workflowStillProcessing;
      $[424] = t164;
    } else {
      t164 = $[424];
    }
    let t165;
    if ($[425] === Symbol.for("react.memo_cache_sentinel")) {
      t165 = <p className="text-sm font-medium">Action Notes</p>;
      $[425] = t165;
    } else {
      t165 = $[425];
    }
    let t166;
    if ($[426] !== actionNote) {
      t166 = <div className="space-y-2 rounded-md border p-3">{t165}<DecisionMetadata items={[{
          label: "Action Notes",
          value: <CopyableContent text={actionNote} textClassName="text-xs" />
        }]} /></div>;
      $[426] = actionNote;
      $[427] = t166;
    } else {
      t166 = $[427];
    }
    let t167;
    if ($[428] !== t163 || $[429] !== t164 || $[430] !== t166) {
      t167 = <div className="space-y-3">{t163}{t164}{t166}</div>;
      $[428] = t163;
      $[429] = t164;
      $[430] = t166;
      $[431] = t167;
    } else {
      t167 = $[431];
    }
    steps = [{
      number: 1,
      title: "Locate the Document in the Patient Chart",
      status: ensurePatientStatus,
      summary: "Copy and search by the document ID in Referral.",
      taskStepKeys: [StepKey.ENSURE_PATIENT],
      content: t31
    }, {
      number: 2,
      title: "Verify the provider and location",
      status: assignmentConfirmationStatus,
      taskStepKeys: [StepKey.RESOLVE_RECEIVING_ASSIGNMENT, StepKey.TPR_CAMELBACK_FOLLOW_THROUGH],
      summary: usesRetiredUsualProviderRule ? "This legacy assignment used Athena's usual provider and must be checked against qualifying appointment history." : assignmentConfirmationStatus === "needs_action" ? "Provider and location require manual review before downstream referral work." : "Verify we selected the correct receiving provider and location.",
      content: t47,
      continuation: receivingAssignmentApproval ? {
        instruction: "After confirming the provider and location in Athena, continue the workflow.",
        control: <TprReceivingAssignmentContinueButton task={task} />
      } : undefined
    }, {
      number: 3,
      title: "Update demographics and care team",
      status: demographicsStatus,
      taskStepKeys: [StepKey.ENSURE_PATIENT, StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM],
      summary: "Review patient details, care team details, and emergency contact separately in Quickview.",
      content: t71
    }, {
      number: 4,
      title: "Insurance",
      status: resolveConsolidatedStatus([coverageStatus, athenaEligibilityStatus, networkStatus]),
      taskStepKeys: [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE, StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY, StepKey.SYNC_ATHENA_COVERAGES, StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS],
      summary: coverageApproval ? coverageApproval.metadata?.instruction ?? "Confirm the Athena coverage before continuing." : coverageReadback?.primary.selfPay ? "Self Pay \u2014 no network or eligibility checks apply." : insuranceRequirements ? `In network: ${inNetworkDisplay}. Athena eligibility: ${insuranceStatusDisplay}.` : coverageReadbackComplete ? "Coverage confirmed. Waiting on Athena eligibility and network/requirements decisions." : assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : "Waiting for the coverage write, Athena eligibility, and network/requirements decisions.",
      content: t92,
      continuation: coverageApproval ? {
        instruction: "After verifying the coverage sequence in Athena, continue the workflow.",
        control: <TprCoverageContinueButton task={task} />
      } : undefined
    }, {
      number: 5,
      title: "Referral Authorization",
      status: t93,
      taskStepKeys: [StepKey.TPR_PCP_REFERRAL_FAX, StepKey.TPR_REFERRAL_AUTHORIZATION],
      summary: t94,
      content: t115,
      continuation: opsKind === "pcp_referral_fax" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After sending the outbound PCP referral fax from Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="PCP referral fax confirmed" successDescription="The workflow is continuing with authorization." />
      } : opsKind === "authorization_review" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After reviewing the Athena referral authorization object, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Authorization reviewed" successDescription="The workflow is continuing with the remaining referral steps." />
      } : opsKind === "manual_authorization" ? {
        instruction: opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION,
        control: <TprOpsContinueButton task={task} successTitle="Manual authorization recorded" successDescription="The workflow is continuing with the remaining referral steps." />
      } : undefined
    }, {
      number: 6,
      title: "Outbound Faxes",
      status: outboundFaxStatus,
      taskStepKeys: [StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "auth_request_upload" ? "Export the patient chart in Athena manually, then upload the request letter. The letter is not uploaded automatically." : outboundFaxStatus === "not_needed" ? "No outbound fax is required for this referral." : "Send the required outbound fax(es) from Athena, then upload where applicable.",
      content: t119,
      continuation: opsKind === "auth_request_upload" ? {
        instruction: "After exporting the chart in Athena, upload the request letter.",
        control: <TprOpsContinueButton task={task} label="Upload request letter" pendingLabel="Uploading..." successTitle="Request letter queued" successDescription="The workflow will write the pending authorization and upload the letter." />
      } : undefined
    }, {
      number: 7,
      title: "Review Full Registration",
      status: fullRegistrationStatus,
      summary: "Open Full Registration and verify communication consent, privacy, and referral source.",
      taskStepKeys: [StepKey.ENSURE_PATIENT],
      content: t127
    }, {
      number: 8,
      title: "Process referral document",
      status: taskStepStatus(taskSteps, [StepKey.REFERRAL_DOCUMENT_METADATA]),
      summary: "Referral metadata is written to Athena in the order ops should verify it.",
      taskStepKeys: [StepKey.REFERRAL_DOCUMENT_METADATA],
      content: t149
    }, {
      number: 9,
      title: "Check document upload",
      status: taskStepStatus(taskSteps, [StepKey.SUPPORTING_SECTIONS_UPLOAD]),
      summary: documentUploadLabels.length ? "Confirm the expected documents were uploaded to Athena." : "No documents were selected for upload.",
      taskStepKeys: [StepKey.SUPPORTING_SECTIONS_UPLOAD],
      content: t153
    }, {
      number: 10,
      title: "Verify in Quickview",
      status: dispositionStatus === "failed" ? "not_run" : notesComplete ? "verify" : "not_run",
      taskStepKeys: [],
      summary: notesComplete ? "Confirm the note above appears correctly on the patient's Quickview tab in Athena." : "Waiting for the final note to be written before this can be verified."
    }, {
      number: 11,
      title: "Bucket Assignment",
      status: resolveConsolidatedStatus([closeDocumentStatus, dispositionStatus === "failed" ? "not_run" : !workflowStillProcessing && notesComplete && (!hasMissingInformation || missingInfoUploadComplete) ? "needs_action" : taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX])]),
      taskStepKeys: [StepKey.TPR_CLOSE_DOCUMENT, StepKey.REFERRAL_DISPOSITION, StepKey.SCHEDULING_RECOMMENDATION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "close_document" ? opsApproval?.metadata?.instruction ?? "There is an upcoming appointment within 90 days. Close the document in Athena, then continue." : hasMissingInformation && !missingInfoUploadComplete ? "Waiting for the missing-information document to be prepared." : referralAuthNeedsPendingUpload && authRequestUploaded ? "Confirm the authorization-request letter on the chart, route, and complete." : hasMissingInformation ? "Send the missing-information document, route the referral, and complete only after all manual actions are done." : "Confirm the recommended destination bucket \u2014 SS Cardio, Biltmore, or Close \u2014 and complete the task.",
      content: t167,
      continuation: opsKind === "close_document" ? {
        instruction: "After closing the document in Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Document close confirmed" successDescription="The workflow is continuing with remaining follow-through." />
      } : verificationActionStepNumber === 11 ? {
        instruction: `After completing the manual follow-through in Athena and assigning ${bucketLabel}, continue to complete the task.`,
        control: <VerificationButtons task={task} continueOnly={true} />
      } : undefined
    }];
    let t168;
    if ($[432] !== taskSteps) {
      t168 = taskSteps.find(_temp24);
      $[432] = taskSteps;
      $[433] = t168;
    } else {
      t168 = $[433];
    }
    const failedTaskStep = t168;
    failedStepCard = failedTaskStep ? steps.find(step_6 => step_6.taskStepKeys.some(stepKey => stepKey === failedTaskStep.stepKey)) : undefined;
    t3 = failedTaskStep && failedStepCard ? failedStepCard.taskStepKeys.find(stepKey_0 => stepKey_0 === failedTaskStep.stepKey) ?? null : null;
    $[6] = actionNote;
    $[7] = bucketLabel;
    $[8] = chartReferralAuths;
    $[9] = chartRequestLetters;
    $[10] = coverageReadback;
    $[11] = decisionTrace;
    $[12] = documentId;
    $[13] = documentUploadLabels;
    $[14] = followThroughEligible;
    $[15] = hasMissingInformation;
    $[16] = insuranceRequirements;
    $[17] = insuranceStatusDisplay;
    $[18] = internalNoteToEnter;
    $[19] = isPrimaryCareProvider;
    $[20] = isProviderFirst;
    $[21] = opsCrossCheck.diagnosis;
    $[22] = opsCrossCheck.emergencyContactName;
    $[23] = opsCrossCheck.emergencyContactPhoneNumber;
    $[24] = opsCrossCheck.emergencyContactRelationship;
    $[25] = opsCrossCheck.isSpecialCaseInsurance;
    $[26] = opsCrossCheck.patientDateOfBirth;
    $[27] = opsCrossCheck.patientEthnicity;
    $[28] = opsCrossCheck.patientFaxNumber;
    $[29] = opsCrossCheck.patientHomeAddress;
    $[30] = opsCrossCheck.patientLanguage;
    $[31] = opsCrossCheck.patientName;
    $[32] = opsCrossCheck.patientPhoneNumber;
    $[33] = opsCrossCheck.patientRace;
    $[34] = opsCrossCheck.primaryPlanLabel;
    $[35] = opsCrossCheck.primarySubscriberId;
    $[36] = opsCrossCheck.receivingProviderName;
    $[37] = opsCrossCheck.receivingProviderNpi;
    $[38] = opsCrossCheck.referralDate;
    $[39] = opsCrossCheck.referringLocationAddress;
    $[40] = opsCrossCheck.referringProviderName;
    $[41] = opsCrossCheck.referringProviderNpi;
    $[42] = opsCrossCheck.referringProviderSpecialty;
    $[43] = patientDisplayId;
    $[44] = patientEhrId;
    $[45] = patientLabel;
    $[46] = pendingApproval;
    $[47] = primaryCareProviderAlreadyPresent;
    $[48] = privacyNoticeOnFile;
    $[49] = providerSelectionEvidence;
    $[50] = receivingAssignmentConfirmed;
    $[51] = referralAuthorization;
    $[52] = referralId;
    $[53] = resolvedReceiving.advisories;
    $[54] = resolvedReceiving.flags;
    $[55] = resolvedReceiving.locationLabel;
    $[56] = resolvedReceiving.providerLabel;
    $[57] = resolvedReceiving.providerNpi;
    $[58] = resolvedReceiving.providerWriteSafe;
    $[59] = resolvedReceiving.routedToSscardio;
    $[60] = resolvedReceiving.usualProviderWriteSafe;
    $[61] = task;
    $[62] = taskSteps;
    $[63] = unresolvedUpcomingProviders;
    $[64] = upcomingAppointmentProviderSummary;
    $[65] = urgency.isUrgent;
    $[66] = urgency.reason;
    $[67] = urgency.schedulingPriority;
    $[68] = workflowStillProcessing;
    $[69] = coverageApproval;
    $[70] = failedStepCard;
    $[71] = opsApproval;
    $[72] = steps;
    $[73] = t3;
    $[74] = waitingForAssociatedReferral;
  } else {
    coverageApproval = $[69];
    failedStepCard = $[70];
    opsApproval = $[71];
    steps = $[72];
    t3 = $[73];
    waitingForAssociatedReferral = $[74];
  }
  const failedStepKey = t3;
  let t4;
  if ($[434] !== coverageApproval || $[435] !== failedStepCard?.number || $[436] !== opsApproval || $[437] !== steps) {
    t4 = coverageApproval ? steps.filter(_temp25) : opsApproval ? steps.filter(_temp26) : steps.filter(step_9 => step_9.number === failedStepCard?.number || step_9.status === "needs_action" || step_9.status === "failed" || step_9.status === "verify");
    $[434] = coverageApproval;
    $[435] = failedStepCard?.number;
    $[436] = opsApproval;
    $[437] = steps;
    $[438] = t4;
  } else {
    t4 = $[438];
  }
  const nextSteps = t4;
  const actionsRemainingVariant = failedStepCard || nextSteps.some(_temp27) ? "destructive" : nextSteps.length || waitingForAssociatedReferral ? "warn" : workflowStillProcessing ? "outline" : "success";
  let t5;
  if ($[439] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ListChecks />;
    $[439] = t5;
  } else {
    t5 = $[439];
  }
  let t6;
  if ($[440] !== failedStepCard?.number || $[441] !== nextSteps || $[442] !== waitingForAssociatedReferral || $[443] !== workflowStillProcessing) {
    t6 = waitingForAssociatedReferral ? <p className="text-sm">Waiting for a matching Referral for this patient. Standalone Referral Auth holds until a Referral in the two-day window finishes syncing.</p> : nextSteps.length ? <ol className="space-y-2 text-sm">{nextSteps.map(step_11 => <li key={step_11.number}><strong>Step {step_11.number}: {step_11.title}</strong><span className="block text-muted-foreground">{step_11.number === failedStepCard?.number ? "Complete this step manually, then continue from its card." : step_11.summary}</span></li>)}</ol> : <p className="text-sm">{workflowStillProcessing ? "Automation is still running. This timeline will update as each durable workflow step completes." : "No manual actions remain."}</p>;
    $[440] = failedStepCard?.number;
    $[441] = nextSteps;
    $[442] = waitingForAssociatedReferral;
    $[443] = workflowStillProcessing;
    $[444] = t6;
  } else {
    t6 = $[444];
  }
  let t7;
  if ($[445] !== actionsRemainingVariant || $[446] !== t6) {
    t7 = <ValerieCard title="Actions remaining" icon={t5} variant={actionsRemainingVariant} contentClassName="space-y-4">{t6}</ValerieCard>;
    $[445] = actionsRemainingVariant;
    $[446] = t6;
    $[447] = t7;
  } else {
    t7 = $[447];
  }
  let t8;
  if ($[448] !== failedStepCard?.number || $[449] !== failedStepKey || $[450] !== steps || $[451] !== task) {
    t8 = steps.map(step_12 => <TimelineStepCard key={step_12.number} task={task} step={step_12} failedStepKey={step_12.number === failedStepCard?.number ? failedStepKey : null} />);
    $[448] = failedStepCard?.number;
    $[449] = failedStepKey;
    $[450] = steps;
    $[451] = task;
    $[452] = t8;
  } else {
    t8 = $[452];
  }
  let t9;
  if ($[453] !== t7 || $[454] !== t8) {
    t9 = <div className="space-y-8 pb-8">{t7}{t8}</div>;
    $[453] = t7;
    $[454] = t8;
    $[455] = t9;
  } else {
    t9 = $[455];
  }
  return t9;
}
function _temp27(step_10) {
  return step_10.status === "failed";
}
function _temp26(step_8) {
  return step_8.continuation;
}
function _temp25(step_7) {
  return step_7.number === 4;
}
function _temp24(step_5) {
  return step_5.status === TaskStepStatus.FAILED;
}
function _temp23(entry, index) {
  return <li key={`${entry.label}-${index}`}>{entry.label} <strong>{entry.outcome}</strong></li>;
}
function _temp22(label) {
  return <li key={label}>{label}</li>;
}
function _temp21(reason_4) {
  return <li key={reason_4}>{reason_4}</li>;
}
function _temp20(letter) {
  return <li key={letter.documentEhrId}><CopyableContent text={letter.documentEhrId} textClassName="text-xs" />{letter.documentDate ? ` · ${letter.documentDate}` : ""}{letter.description ? ` · ${letter.description}` : ""}</li>;
}
function _temp19(authorization) {
  return <li key={authorization.referralAuthId}><CopyableContent text={authorization.referralAuthId} textClassName="text-xs" />{authorization.number ? ` · ${authorization.number}` : ""}{authorization.startDate || authorization.endDate ? ` · ${authorization.startDate ?? "\u2014"}–${authorization.endDate ?? "\u2014"}` : ""}{authorization.expired ? " \xB7 expired" : ""}</li>;
}
function _temp18(reason_3) {
  return <li key={reason_3}>{reason_3}</li>;
}
function _temp17(reason_2) {
  return <li key={reason_2}>{reason_2}</li>;
}
function _temp16(provider) {
  return <Badge key={provider.providerEhrId ?? provider.label} variant="outline">{provider.label}{provider.providerEhrId ? ` · Athena ID ${provider.providerEhrId}` : ""}</Badge>;
}
function _temp15(reason_1) {
  return <li key={reason_1}>{reason_1}</li>;
}
function _temp14(advisory) {
  return <li key={advisory}>{advisory}</li>;
}
function _temp13(coverage) {
  return <p key={coverage.insuranceId}><strong>{coverage.casePolicy ? "Case policy" : coverage.sequence === 1 ? "Primary" : coverage.sequence === 2 ? "Secondary" : `Sequence ${coverage.sequence ?? "N/A"}`}:</strong>{" "}{coverage.planName ?? "Unknown plan"} · Package <CopyableContent text={coverage.insurancePackageId} />{coverage.subscriberId ? <>{" "}· Subscriber <CopyableContent text={coverage.subscriberId} /></> : null}</p>;
}
function _temp12(reason_0) {
  return reason_0 !== null;
}
function _temp11(reason) {
  return reason !== null;
}
function _temp10(file) {
  return file.type === FileType.INBOUND;
}
function _temp9(statuses) {
  if (statuses.includes("failed")) {
    return "failed";
  }
  if (statuses.includes("needs_action")) {
    return "needs_action";
  }
  if (statuses.includes("verify")) {
    return "verify";
  }
  if (statuses.includes("waiting")) {
    return "waiting";
  }
  if (statuses.includes("verified")) {
    return "verified";
  }
  if (statuses.includes("automated")) {
    return "automated";
  }
  if (statuses.includes("not_run")) {
    return "not_run";
  }
  return "not_needed";
}
function _temp8(step_4) {
  return step_4.stepKey === StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX && step_4.status === TaskStepStatus.COMPLETED;
}
function _temp7(step_3) {
  return step_3.stepKey === StepKey.REFERRAL_DISPOSITION && step_3.status === TaskStepStatus.COMPLETED;
}
function _temp6(step_2) {
  return step_2.stepKey === StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD && step_2.status === TaskStepStatus.COMPLETED && step_2.output?.uploaded === true;
}
function _temp5(step_1) {
  return step_1.stepKey === StepKey.READ_ATHENA_COVERAGE && step_1.status === TaskStepStatus.COMPLETED;
}
function _temp4(step_0) {
  return step_0.stepKey === StepKey.TPR_WAIT_FOR_ASSOCIATED_REFERRAL && step_0.status === TaskStepStatus.PAUSED;
}
function _temp3(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT;
}
interface TprReferralInstructionsProps {
  view: "information" | "steps";
  referral: Referral;
  task: Task;
  athenaData: AthenaReferralCardsData | null;
  patientEhrId: string;
  /** "New Patient Referral" / "Established Patient Referral" / "N/A" — see getAthenaReferralDocumentLabel. */
  patientLabel: string;
  upcomingAppointments: Appointment[];
  /** Past booked appointments (lookback window matching the new/established determination) for the
   * "Patient Type" sanity-check list — display-only, not the source of the New/Established label itself. */
  pastAppointments: Appointment[];
  documentId: string;
  isPrimaryCareProvider: boolean;
  /** Whether the referring provider is already on the Athena care team as PRIMARY_CARE_PROVIDER —
   * suppresses the redundant "PCP" line in Step 3's care team card. See resolveTprCareTeamPolicy. */
  primaryCareProviderAlreadyPresent: boolean;
  /** One of TprSyncBucket's four values: "closed" | "sscardio" | "biltmorecardreferral" | "biltmorecardiosched" */
  bucketLabel: string;
  actionNote: string;
  /** Athena document priority (Urgent vs Normal) and why — shown in Step 8 before the internal note. */
  urgency: TprSyncChecklistUrgency;
  /** The note the disposition step will write, always carrying the
   * "Reviewed By Valerie Health (date) - <provider> - ..." prefix. Step 8 shows this rather than the
   * Athena read-back, which is still empty at that point in the workflow. */
  expectedInternalNote: string;
  /** Each decision-tree check that ran, in order, with its outcome — see resolveTprSyncChecklist. */
  decisionTrace: TprSyncChecklistTraceEntry[];
  /** Non-stopping context notes from the decision tree (insurance-sync issues, urgency reason, the
   * detailed "ops must manually verify..." explanation behind a short bucket-assignment label, etc). */
  flags: string[];
  /** "Eligible" | "Ineligible" | "Unverified" — same Athena-then-Stedi result the decision tree reads. */
  eligibilityDisplay: StediEligibilityDisplayLabel;
  documentUploadLabels: string[];
  privacyNoticeOnFile: boolean | null;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  receivingAssignmentConfirmed: boolean;
  followThroughEligible: boolean;
  coverageReadback: AthenaCoverageReadbackOutput | null;
  insuranceRequirements: TprInsuranceRequirementsOutput | null;
  referralAuthorization: TprReferralAuthorizationOutput | null;
  unresolvedUpcomingProviders: Array<{
    label: string;
    providerEhrId: string | null;
  }>;
  upcomingAppointmentProviderSummary: string | null;
  /** Persisted provider-first assignment, or the legacy display fallback for older tasks. */
  resolvedReceiving: {
    providerLabel: string | null;
    providerNpi: string | null;
    providerWriteSafe: boolean;
    usualProviderWriteSafe: boolean;
    locationLabel: string | null;
    trace: string[];
    flags: string[];
    /** Non-blocking reviewer notices (e.g. the Terrace cardiology/neurology check) — always shown,
     * unlike `flags`, which only renders once the assignment already needs manual review. */
    advisories: string[];
    routedToSscardio: boolean;
    providerSelectionEvidence?: ReceivingAssignmentOutput["providerSelectionEvidence"];
  };
  chartReferralAuths: TprChartReferralAuth[];
  chartRequestLetters: TprChartRequestLetter[];
}
export function TprReferralInstructions(t0) {
  const $ = _c(420);
  const {
    view,
    referral,
    task,
    athenaData,
    patientEhrId,
    patientLabel,
    upcomingAppointments,
    pastAppointments,
    documentId,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    bucketLabel,
    actionNote,
    urgency,
    expectedInternalNote,
    decisionTrace,
    flags,
    eligibilityDisplay,
    documentUploadLabels,
    privacyNoticeOnFile,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    resolvedReceiving,
    chartReferralAuths,
    chartRequestLetters
  } = t0;
  let t1;
  if ($[0] !== referral.referringLocation?.address) {
    t1 = getFormattedAddress(referral.referringLocation?.address) || NOT_AVAILABLE;
    $[0] = referral.referringLocation?.address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referringLocationAddress = t1;
  const referringProviderSpecialty = referral.referringProvider?.specialty || NOT_AVAILABLE;
  let t2;
  if ($[2] !== referral.referringProvider) {
    t2 = referral.referringProvider ? getPractitionerNameWithCredentials(referral.referringProvider) : NOT_AVAILABLE;
    $[2] = referral.referringProvider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const referringProviderName = t2;
  let t3;
  if ($[4] !== referral.receivingProvider) {
    t3 = referral.receivingProvider ? getPractitionerNameWithCredentials(referral.receivingProvider) : NOT_AVAILABLE;
    $[4] = referral.receivingProvider;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const receivingProviderName = t3;
  const patient = referral.patient;
  const emergencyContactName = patient?.emergencyContactName || NOT_AVAILABLE;
  const emergencyContactPhoneNumber = patient?.emergencyContactPhoneNumber || NOT_AVAILABLE;
  const emergencyContactRelationship = patient?.emergencyContactRelationshipToPatient || NOT_AVAILABLE;
  let t4;
  if ($[6] !== referral.primaryCoverage) {
    t4 = getCoverageSummary(referral.primaryCoverage);
    $[6] = referral.primaryCoverage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const primaryCoverageSummary = t4;
  let t5;
  if ($[8] !== referral.secondaryCoverage) {
    t5 = getCoverageSummary(referral.secondaryCoverage);
    $[8] = referral.secondaryCoverage;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const secondaryCoverageSummary = t5;
  let t6;
  if ($[10] !== referral.dateOfReferral) {
    t6 = formatDateMmDdYyyy(referral.dateOfReferral) || NOT_AVAILABLE;
    $[10] = referral.dateOfReferral;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const dateOfReferral = t6;
  const schedulingPriority = referral.schedulingPriority || NOT_AVAILABLE;
  const athenaInternalNote = athenaData?.document?.internalNote || NOT_AVAILABLE;
  const internalNoteToEnter = athenaInternalNote.toLowerCase().includes(REVIEWED_BY_VALERIE_HEALTH_MARKER) ? athenaInternalNote : expectedInternalNote;
  let T0;
  let dataSourceEntries;
  let hasMissingInformation;
  let missingFields;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t7;
  let t8;
  let t9;
  if ($[12] !== actionNote || $[13] !== athenaData?.coverages || $[14] !== athenaData?.patientDetails || $[15] !== bucketLabel || $[16] !== chartReferralAuths || $[17] !== chartRequestLetters || $[18] !== coverageReadback || $[19] !== dateOfReferral || $[20] !== decisionTrace || $[21] !== documentId || $[22] !== documentUploadLabels || $[23] !== eligibilityDisplay || $[24] !== emergencyContactName || $[25] !== emergencyContactPhoneNumber || $[26] !== emergencyContactRelationship || $[27] !== followThroughEligible || $[28] !== insuranceRequirements || $[29] !== internalNoteToEnter || $[30] !== isPrimaryCareProvider || $[31] !== pastAppointments || $[32] !== patient || $[33] !== patientEhrId || $[34] !== patientLabel || $[35] !== pendingApproval || $[36] !== primaryCareProviderAlreadyPresent || $[37] !== primaryCoverageSummary || $[38] !== privacyNoticeOnFile || $[39] !== receivingAssignmentConfirmed || $[40] !== receivingProviderName || $[41] !== referral.diagnosisList || $[42] !== referral.id || $[43] !== referral.primaryCoverage || $[44] !== referral.reasonForReferral || $[45] !== referral.receivingLocation?.name || $[46] !== referral.receivingProvider || $[47] !== referral.referringLocation?.name || $[48] !== referral.referringProvider || $[49] !== referralAuthorization || $[50] !== referringLocationAddress || $[51] !== referringProviderName || $[52] !== referringProviderSpecialty || $[53] !== resolvedReceiving || $[54] !== secondaryCoverageSummary || $[55] !== task || $[56] !== taskSteps || $[57] !== unresolvedUpcomingProviders || $[58] !== upcomingAppointmentProviderSummary || $[59] !== upcomingAppointments || $[60] !== urgency || $[61] !== view) {
    t25 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedPastAppointments = sortAppointmentsMostRecentFirst(pastAppointments);
      const syncFailed = task.status === TaskStatus.FAILED;
      const insuranceStatusDisplay = coverageReadback?.primary.selfPay ? "Self Pay" : referral.primaryCoverage ? eligibilityDisplay : "Missing Insurance";
      const chartPatient = athenaData?.patientDetails ?? null;
      let t26;
      if ($[85] !== patient) {
        t26 = patient && getPatientPhoneNumber(patient)?.trim() || null;
        $[85] = patient;
        $[86] = t26;
      } else {
        t26 = $[86];
      }
      const referralPhone = t26;
      let t27;
      if ($[87] !== chartPatient) {
        t27 = chartPatient && getPatientPhoneNumber(chartPatient)?.trim() || null;
        $[87] = chartPatient;
        $[88] = t27;
      } else {
        t27 = $[88];
      }
      const chartPhone = t27;
      let chartAddressValue;
      let phoneDataSourceEntry;
      let referralAddressValue;
      let t28;
      let t29;
      let t30;
      if ($[89] !== athenaData?.coverages || $[90] !== chartPatient || $[91] !== chartPhone || $[92] !== patient || $[93] !== referral.primaryCoverage || $[94] !== referralPhone) {
        const referralCoverageValue = getCoverageSummary(referral.primaryCoverage);
        const chartPrimaryCoverage = [...(athenaData?.coverages ?? [])].sort(_temp28)[0];
        const chartCoverageValue = getCoverageSummary(chartPrimaryCoverage ?? null);
        referralAddressValue = patient?.homeAddress ? getFormattedAddress(patient.homeAddress) : null;
        chartAddressValue = chartPatient?.homeAddress ? getFormattedAddress(chartPatient.homeAddress) : null;
        let t31;
        if ($[101] !== chartPhone || $[102] !== referralPhone) {
          t31 = resolveDataSourceEntry("Phone Number", referralPhone, chartPhone);
          $[101] = chartPhone;
          $[102] = referralPhone;
          $[103] = t31;
        } else {
          t31 = $[103];
        }
        phoneDataSourceEntry = t31;
        t28 = phoneDataSourceEntry;
        t29 = resolveDataSourceEntry("Insurance", referralCoverageValue, chartCoverageValue);
        t30 = resolveDataSourceEntry("Home Address", referralAddressValue, chartAddressValue);
        $[89] = athenaData?.coverages;
        $[90] = chartPatient;
        $[91] = chartPhone;
        $[92] = patient;
        $[93] = referral.primaryCoverage;
        $[94] = referralPhone;
        $[95] = chartAddressValue;
        $[96] = phoneDataSourceEntry;
        $[97] = referralAddressValue;
        $[98] = t28;
        $[99] = t29;
        $[100] = t30;
      } else {
        chartAddressValue = $[95];
        phoneDataSourceEntry = $[96];
        referralAddressValue = $[97];
        t28 = $[98];
        t29 = $[99];
        t30 = $[100];
      }
      let t31;
      if ($[104] !== t28 || $[105] !== t29 || $[106] !== t30) {
        t31 = [t28, t29, t30];
        $[104] = t28;
        $[105] = t29;
        $[106] = t30;
        $[107] = t31;
      } else {
        t31 = $[107];
      }
      dataSourceEntries = t31;
      const isCamelbackReferral = !resolvedReceiving.routedToSscardio;
      let t32;
      if ($[108] !== isCamelbackReferral || $[109] !== phoneDataSourceEntry) {
        t32 = isCamelbackReferral && phoneDataSourceEntry.missing ? [phoneDataSourceEntry] : [];
        $[108] = isCamelbackReferral;
        $[109] = phoneDataSourceEntry;
        $[110] = t32;
      } else {
        t32 = $[110];
      }
      missingFields = t32;
      hasMissingInformation = missingFields.length > 0;
      let t33;
      if ($[111] !== patient) {
        t33 = getPatientDisplayName(patient);
        $[111] = patient;
        $[112] = t33;
      } else {
        t33 = $[112];
      }
      const patientDisplayName = t33;
      const receivingLocationName = referral.receivingLocation?.name || NOT_AVAILABLE;
      let t34;
      if ($[113] !== referral.diagnosisList || $[114] !== referral.reasonForReferral) {
        t34 = [referral.reasonForReferral, ...((referral.diagnosisList ?? []) as DiagnosisItem[]).map(_temp29)].filter(_temp30).filter(_temp31).join(", ") || NOT_AVAILABLE;
        $[113] = referral.diagnosisList;
        $[114] = referral.reasonForReferral;
        $[115] = t34;
      } else {
        t34 = $[115];
      }
      const diagnosis = t34;
      let t35;
      if ($[116] !== patient) {
        t35 = patient ? getPatientName(patient) || NOT_AVAILABLE : NOT_AVAILABLE;
        $[116] = patient;
        $[117] = t35;
      } else {
        t35 = $[117];
      }
      let t36;
      if ($[118] !== patient?.dateOfBirth) {
        t36 = formatDateMmDdYyyy(patient?.dateOfBirth) || NOT_AVAILABLE;
        $[118] = patient?.dateOfBirth;
        $[119] = t36;
      } else {
        t36 = $[119];
      }
      const t37 = chartPhone ?? referralPhone ?? NOT_AVAILABLE;
      let t38;
      if ($[120] !== patient) {
        t38 = patient && getPatientFaxNumber(patient) || NOT_AVAILABLE;
        $[120] = patient;
        $[121] = t38;
      } else {
        t38 = $[121];
      }
      const t39 = patient?.ethnicity || NOT_AVAILABLE;
      const t40 = patient?.race || NOT_AVAILABLE;
      const t41 = patient?.language || NOT_AVAILABLE;
      const t42 = chartAddressValue ?? referralAddressValue ?? NOT_AVAILABLE;
      const t43 = referral.receivingProvider?.npiNumber || NOT_AVAILABLE;
      const t44 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      const t45 = referral.referringLocation?.name || NOT_AVAILABLE;
      let t46;
      if ($[122] !== referral.primaryCoverage) {
        t46 = referral.primaryCoverage ? getCoverageDisplayName({
          coverage: referral.primaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : "Self Pay";
        $[122] = referral.primaryCoverage;
        $[123] = t46;
      } else {
        t46 = $[123];
      }
      const t47 = referral.primaryCoverage?.subscriberId || NOT_AVAILABLE;
      const t48 = referral.primaryCoverage?.payer;
      let t49;
      if ($[124] !== t48) {
        t49 = isTprSpecialCaseInsurance(t48);
        $[124] = t48;
        $[125] = t49;
      } else {
        t49 = $[125];
      }
      let t50;
      if ($[126] !== dateOfReferral || $[127] !== diagnosis || $[128] !== emergencyContactName || $[129] !== emergencyContactPhoneNumber || $[130] !== emergencyContactRelationship || $[131] !== receivingProviderName || $[132] !== referringLocationAddress || $[133] !== referringProviderName || $[134] !== referringProviderSpecialty || $[135] !== t35 || $[136] !== t36 || $[137] !== t37 || $[138] !== t38 || $[139] !== t39 || $[140] !== t40 || $[141] !== t41 || $[142] !== t42 || $[143] !== t43 || $[144] !== t44 || $[145] !== t45 || $[146] !== t46 || $[147] !== t47 || $[148] !== t49) {
        t50 = {
          patientName: t35,
          patientDateOfBirth: t36,
          patientPhoneNumber: t37,
          patientFaxNumber: t38,
          patientEthnicity: t39,
          patientRace: t40,
          patientLanguage: t41,
          patientHomeAddress: t42,
          receivingProviderName,
          receivingProviderNpi: t43,
          referringProviderName,
          referringProviderNpi: t44,
          referringProviderSpecialty,
          referringLocationName: t45,
          referringLocationAddress,
          emergencyContactName,
          emergencyContactPhoneNumber,
          emergencyContactRelationship,
          referralDate: dateOfReferral,
          primaryPlanLabel: t46,
          primarySubscriberId: t47,
          isSpecialCaseInsurance: t49,
          diagnosis
        };
        $[126] = dateOfReferral;
        $[127] = diagnosis;
        $[128] = emergencyContactName;
        $[129] = emergencyContactPhoneNumber;
        $[130] = emergencyContactRelationship;
        $[131] = receivingProviderName;
        $[132] = referringLocationAddress;
        $[133] = referringProviderName;
        $[134] = referringProviderSpecialty;
        $[135] = t35;
        $[136] = t36;
        $[137] = t37;
        $[138] = t38;
        $[139] = t39;
        $[140] = t40;
        $[141] = t41;
        $[142] = t42;
        $[143] = t43;
        $[144] = t44;
        $[145] = t45;
        $[146] = t46;
        $[147] = t47;
        $[148] = t49;
        $[149] = t50;
      } else {
        t50 = $[149];
      }
      const opsCrossCheck = t50;
      if (view === "steps") {
        let t51;
        if ($[150] !== actionNote || $[151] !== bucketLabel || $[152] !== chartReferralAuths || $[153] !== chartRequestLetters || $[154] !== coverageReadback || $[155] !== decisionTrace || $[156] !== documentId || $[157] !== documentUploadLabels || $[158] !== followThroughEligible || $[159] !== hasMissingInformation || $[160] !== insuranceRequirements || $[161] !== insuranceStatusDisplay || $[162] !== internalNoteToEnter || $[163] !== isPrimaryCareProvider || $[164] !== opsCrossCheck || $[165] !== pastAppointments || $[166] !== patient.displayId || $[167] !== patientEhrId || $[168] !== patientLabel || $[169] !== pendingApproval || $[170] !== primaryCareProviderAlreadyPresent || $[171] !== privacyNoticeOnFile || $[172] !== receivingAssignmentConfirmed || $[173] !== referral.id || $[174] !== referralAuthorization || $[175] !== resolvedReceiving || $[176] !== task || $[177] !== taskSteps || $[178] !== unresolvedUpcomingProviders || $[179] !== upcomingAppointmentProviderSummary || $[180] !== upcomingAppointments || $[181] !== urgency) {
          t51 = <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto"><TprSyncTimeline task={task} referralId={referral.id} taskSteps={taskSteps} pendingApproval={pendingApproval} receivingAssignmentConfirmed={receivingAssignmentConfirmed} followThroughEligible={followThroughEligible} coverageReadback={coverageReadback} insuranceRequirements={insuranceRequirements} referralAuthorization={referralAuthorization} resolvedReceiving={resolvedReceiving} patientLabel={patientLabel} patientEhrId={patientEhrId} documentId={documentId} bucketLabel={bucketLabel} internalNoteToEnter={internalNoteToEnter} urgency={urgency} actionNote={actionNote} decisionTrace={decisionTrace} insuranceStatusDisplay={insuranceStatusDisplay} hasMissingInformation={hasMissingInformation} unresolvedUpcomingProviders={unresolvedUpcomingProviders} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} upcomingAppointments={upcomingAppointments} pastAppointments={pastAppointments} documentUploadLabels={documentUploadLabels} privacyNoticeOnFile={privacyNoticeOnFile} isPrimaryCareProvider={isPrimaryCareProvider} primaryCareProviderAlreadyPresent={primaryCareProviderAlreadyPresent} patientDisplayId={patient.displayId} opsCrossCheck={opsCrossCheck} chartReferralAuths={chartReferralAuths} chartRequestLetters={chartRequestLetters} /></div>;
          $[150] = actionNote;
          $[151] = bucketLabel;
          $[152] = chartReferralAuths;
          $[153] = chartRequestLetters;
          $[154] = coverageReadback;
          $[155] = decisionTrace;
          $[156] = documentId;
          $[157] = documentUploadLabels;
          $[158] = followThroughEligible;
          $[159] = hasMissingInformation;
          $[160] = insuranceRequirements;
          $[161] = insuranceStatusDisplay;
          $[162] = internalNoteToEnter;
          $[163] = isPrimaryCareProvider;
          $[164] = opsCrossCheck;
          $[165] = pastAppointments;
          $[166] = patient.displayId;
          $[167] = patientEhrId;
          $[168] = patientLabel;
          $[169] = pendingApproval;
          $[170] = primaryCareProviderAlreadyPresent;
          $[171] = privacyNoticeOnFile;
          $[172] = receivingAssignmentConfirmed;
          $[173] = referral.id;
          $[174] = referralAuthorization;
          $[175] = resolvedReceiving;
          $[176] = task;
          $[177] = taskSteps;
          $[178] = unresolvedUpcomingProviders;
          $[179] = upcomingAppointmentProviderSummary;
          $[180] = upcomingAppointments;
          $[181] = urgency;
          $[182] = t51;
        } else {
          t51 = $[182];
        }
        t25 = t51;
        break bb0;
      }
      t24 = "flex flex-col gap-8 sm:min-w-[800px] w-full max-w-2xl mx-auto";
      T0 = EhrInstructionsCard;
      t23 = "Detailed Athena verification reference";
      t16 = "text-sm text-muted-foreground space-y-6";
      if ($[183] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = <h3 className="text-md font-semibold text-center text-foreground">TPR Biltmore Cardiology Sync Verification</h3>;
        $[183] = t17;
      } else {
        t17 = $[183];
      }
      const t51 = syncFailed ? "Automation did not complete for this task. Resolve the sync error below and verify Athena before closing." : "Automation has already run for this task. Use this checklist to verify the sync in Athena before closing.";
      if ($[184] !== t51) {
        t18 = <p className="text-xs text-center">{t51}</p>;
        $[184] = t51;
        $[185] = t18;
      } else {
        t18 = $[185];
      }
      if ($[186] !== task.id) {
        t19 = <SyncIncompleteActions taskId={task.id} />;
        $[186] = task.id;
        $[187] = t19;
      } else {
        t19 = $[187];
      }
      let t52;
      if ($[188] === Symbol.for("react.memo_cache_sentinel")) {
        t52 = <strong>Camelback Referral:</strong>;
        $[188] = t52;
      } else {
        t52 = $[188];
      }
      const t53 = isCamelbackReferral ? "Yes" : "No";
      let t54;
      if ($[189] !== t53) {
        t54 = <p>{t52} {t53}</p>;
        $[189] = t53;
        $[190] = t54;
      } else {
        t54 = $[190];
      }
      let t55;
      if ($[191] === Symbol.for("react.memo_cache_sentinel")) {
        t55 = <strong>Patient:</strong>;
        $[191] = t55;
      } else {
        t55 = $[191];
      }
      let t56;
      if ($[192] !== patientDisplayName) {
        t56 = <CopyableContent text={patientDisplayName} />;
        $[192] = patientDisplayName;
        $[193] = t56;
      } else {
        t56 = $[193];
      }
      let t57;
      if ($[194] !== patientLabel || $[195] !== t56) {
        t57 = <p>{t55} {t56} ({patientLabel})</p>;
        $[194] = patientLabel;
        $[195] = t56;
        $[196] = t57;
      } else {
        t57 = $[196];
      }
      let t58;
      if ($[197] === Symbol.for("react.memo_cache_sentinel")) {
        t58 = <strong>Receiving Provider (as extracted):</strong>;
        $[197] = t58;
      } else {
        t58 = $[197];
      }
      let t59;
      if ($[198] !== referral.receivingProvider) {
        t59 = <ProviderCopyFields provider={referral.receivingProvider} />;
        $[198] = referral.receivingProvider;
        $[199] = t59;
      } else {
        t59 = $[199];
      }
      let t60;
      if ($[200] === Symbol.for("react.memo_cache_sentinel")) {
        t60 = <strong>Location:</strong>;
        $[200] = t60;
      } else {
        t60 = $[200];
      }
      let t61;
      if ($[201] !== receivingLocationName) {
        t61 = <CopyableContent text={receivingLocationName} />;
        $[201] = receivingLocationName;
        $[202] = t61;
      } else {
        t61 = $[202];
      }
      let t62;
      if ($[203] !== t59 || $[204] !== t61) {
        t62 = <p>{t58}{" "}{t59}, {t60}{" "}{t61}</p>;
        $[203] = t59;
        $[204] = t61;
        $[205] = t62;
      } else {
        t62 = $[205];
      }
      if ($[206] !== t54 || $[207] !== t57 || $[208] !== t62) {
        t20 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t54}{t57}{t62}</div>;
        $[206] = t54;
        $[207] = t57;
        $[208] = t62;
        $[209] = t20;
      } else {
        t20 = $[209];
      }
      let t63;
      let t64;
      if ($[210] === Symbol.for("react.memo_cache_sentinel")) {
        t63 = <p className="font-semibold">Recommended Receiving Provider / Location</p>;
        t64 = <strong>Provider:</strong>;
        $[210] = t63;
        $[211] = t64;
      } else {
        t63 = $[210];
        t64 = $[211];
      }
      const t65 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
      let t66;
      if ($[212] !== t65) {
        t66 = <CopyableContent text={t65} />;
        $[212] = t65;
        $[213] = t66;
      } else {
        t66 = $[213];
      }
      let t67;
      if ($[214] === Symbol.for("react.memo_cache_sentinel")) {
        t67 = <strong>Location:</strong>;
        $[214] = t67;
      } else {
        t67 = $[214];
      }
      const t68 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
      let t69;
      if ($[215] !== t68) {
        t69 = <CopyableContent text={t68} />;
        $[215] = t68;
        $[216] = t69;
      } else {
        t69 = $[216];
      }
      let t70;
      if ($[217] !== t66 || $[218] !== t69) {
        t70 = <p>{t64} {t66},{" "}{t67} {t69}</p>;
        $[217] = t66;
        $[218] = t69;
        $[219] = t70;
      } else {
        t70 = $[219];
      }
      let t71;
      if ($[220] !== resolvedReceiving.routedToSscardio) {
        t71 = resolvedReceiving.routedToSscardio && <p className="text-xs">Routing: <strong>sscardio</strong></p>;
        $[220] = resolvedReceiving.routedToSscardio;
        $[221] = t71;
      } else {
        t71 = $[221];
      }
      let t72;
      if ($[222] !== resolvedReceiving.flags) {
        t72 = resolvedReceiving.flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{resolvedReceiving.flags.map(_temp32)}</ul>;
        $[222] = resolvedReceiving.flags;
        $[223] = t72;
      } else {
        t72 = $[223];
      }
      let t73;
      if ($[224] !== resolvedReceiving.trace) {
        t73 = resolvedReceiving.trace.length > 0 && <details className="text-xs"><summary>Why</summary><ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{resolvedReceiving.trace.map(_temp33)}</ul></details>;
        $[224] = resolvedReceiving.trace;
        $[225] = t73;
      } else {
        t73 = $[225];
      }
      if ($[226] !== t70 || $[227] !== t71 || $[228] !== t72 || $[229] !== t73) {
        t21 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t63}{t70}{t71}{t72}{t73}</div>;
        $[226] = t70;
        $[227] = t71;
        $[228] = t72;
        $[229] = t73;
        $[230] = t21;
      } else {
        t21 = $[230];
      }
      let t74;
      if ($[231] === Symbol.for("react.memo_cache_sentinel")) {
        t74 = <h4 className="font-semibold text-foreground mb-2">Patient Chart Create</h4>;
        $[231] = t74;
      } else {
        t74 = $[231];
      }
      let t75;
      if ($[232] !== documentId) {
        t75 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[232] = documentId;
        $[233] = t75;
      } else {
        t75 = $[233];
      }
      let t76;
      if ($[234] !== patientEhrId) {
        t76 = <li>Add patient EHR ID if not already added: <CopyableContent text={patientEhrId} /></li>;
        $[234] = patientEhrId;
        $[235] = t76;
      } else {
        t76 = $[235];
      }
      let t77;
      if ($[236] === Symbol.for("react.memo_cache_sentinel")) {
        t77 = <li>Hit save and proceed to quickview.</li>;
        $[236] = t77;
      } else {
        t77 = $[236];
      }
      let t78;
      if ($[237] !== resolvedReceiving.providerLabel || $[238] !== resolvedReceiving.providerNpi || $[239] !== resolvedReceiving.usualProviderWriteSafe) {
        t78 = resolvedReceiving.usualProviderWriteSafe ? <ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={true} /> : "Leave unchanged";
        $[237] = resolvedReceiving.providerLabel;
        $[238] = resolvedReceiving.providerNpi;
        $[239] = resolvedReceiving.usualProviderWriteSafe;
        $[240] = t78;
      } else {
        t78 = $[240];
      }
      const t79 = resolvedReceiving.usualProviderWriteSafe ? "New patient \u2014 add the usual provider to the care team." : "Established patient \u2014 left unchanged to preserve continuity of care.";
      let t80;
      if ($[241] !== t79) {
        t80 = <p className="text-xs italic mt-1">{t79}</p>;
        $[241] = t79;
        $[242] = t80;
      } else {
        t80 = $[242];
      }
      let t81;
      if ($[243] !== t78 || $[244] !== t80) {
        t81 = <li>Usual Provider:{" "}{t78}{t80}</li>;
        $[243] = t78;
        $[244] = t80;
        $[245] = t81;
      } else {
        t81 = $[245];
      }
      let t82;
      if ($[246] !== referral.referringProvider) {
        t82 = <ProviderCopyFields provider={referral.referringProvider} />;
        $[246] = referral.referringProvider;
        $[247] = t82;
      } else {
        t82 = $[247];
      }
      let t83;
      if ($[248] !== referringProviderSpecialty) {
        t83 = <CopyableContent text={referringProviderSpecialty} />;
        $[248] = referringProviderSpecialty;
        $[249] = t83;
      } else {
        t83 = $[249];
      }
      let t84;
      if ($[250] !== t82 || $[251] !== t83) {
        t84 = <li>Referring Provider: {t82}, Specialty:{" "}{t83}</li>;
        $[250] = t82;
        $[251] = t83;
        $[252] = t84;
      } else {
        t84 = $[252];
      }
      let t85;
      if ($[253] !== isPrimaryCareProvider || $[254] !== referral.referringProvider) {
        t85 = isPrimaryCareProvider && <li>PCP: <ProviderCopyFields provider={referral.referringProvider} /></li>;
        $[253] = isPrimaryCareProvider;
        $[254] = referral.referringProvider;
        $[255] = t85;
      } else {
        t85 = $[255];
      }
      let t86;
      if ($[256] !== referral.referringProvider || $[257] !== referringLocationAddress) {
        t86 = referral.referringProvider && <li>Referring provider address: <CopyableContent text={referringLocationAddress} /></li>;
        $[256] = referral.referringProvider;
        $[257] = referringLocationAddress;
        $[258] = t86;
      } else {
        t86 = $[258];
      }
      let t87;
      if ($[259] !== t81 || $[260] !== t84 || $[261] !== t85 || $[262] !== t86) {
        t87 = <li>Check the Patient Care Team:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t81}{t84}{t85}{t86}</ul></li>;
        $[259] = t81;
        $[260] = t84;
        $[261] = t85;
        $[262] = t86;
        $[263] = t87;
      } else {
        t87 = $[263];
      }
      let t88;
      if ($[264] !== emergencyContactName) {
        t88 = <li>Name: <CopyableContent text={emergencyContactName} /></li>;
        $[264] = emergencyContactName;
        $[265] = t88;
      } else {
        t88 = $[265];
      }
      let t89;
      if ($[266] !== emergencyContactPhoneNumber) {
        t89 = <li>Phone Number: <CopyableContent text={emergencyContactPhoneNumber} /></li>;
        $[266] = emergencyContactPhoneNumber;
        $[267] = t89;
      } else {
        t89 = $[267];
      }
      let t90;
      if ($[268] !== emergencyContactRelationship) {
        t90 = <li>Relationship to Patient: <CopyableContent text={emergencyContactRelationship} /></li>;
        $[268] = emergencyContactRelationship;
        $[269] = t90;
      } else {
        t90 = $[269];
      }
      let t91;
      if ($[270] !== t88 || $[271] !== t89 || $[272] !== t90) {
        t91 = <li>Check the Emergency Contact<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t88}{t89}{t90}</ul></li>;
        $[270] = t88;
        $[271] = t89;
        $[272] = t90;
        $[273] = t91;
      } else {
        t91 = $[273];
      }
      const t92 = primaryCoverageSummary ?? "N/A";
      let t93;
      if ($[274] !== t92) {
        t93 = <li>Primary: <CopyableContent text={t92} /></li>;
        $[274] = t92;
        $[275] = t93;
      } else {
        t93 = $[275];
      }
      let t94;
      if ($[276] !== secondaryCoverageSummary) {
        t94 = secondaryCoverageSummary && <li>Secondary: <CopyableContent text={secondaryCoverageSummary} /></li>;
        $[276] = secondaryCoverageSummary;
        $[277] = t94;
      } else {
        t94 = $[277];
      }
      let t95;
      let t96;
      if ($[278] !== insuranceStatusDisplay) {
        t95 = <CopyableContent text={insuranceStatusDisplay} />;
        t96 = insuranceStatusDisplay === "Missing Insurance" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[278] = insuranceStatusDisplay;
        $[279] = t95;
        $[280] = t96;
      } else {
        t95 = $[279];
        t96 = $[280];
      }
      let t97;
      if ($[281] !== insuranceStatusDisplay) {
        t97 = insuranceStatusDisplay === "Unverified" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[281] = insuranceStatusDisplay;
        $[282] = t97;
      } else {
        t97 = $[282];
      }
      let t98;
      if ($[283] !== t95 || $[284] !== t96 || $[285] !== t97) {
        t98 = <li>Eligibility check result: {t95}{t96}{t97}</li>;
        $[283] = t95;
        $[284] = t96;
        $[285] = t97;
        $[286] = t98;
      } else {
        t98 = $[286];
      }
      let t99;
      if ($[287] !== t93 || $[288] !== t94 || $[289] !== t98) {
        t99 = <li>Check the patient insurance<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t93}{t94}{t98}</ul></li>;
        $[287] = t93;
        $[288] = t94;
        $[289] = t98;
        $[290] = t99;
      } else {
        t99 = $[290];
      }
      let t100;
      if ($[291] === Symbol.for("react.memo_cache_sentinel")) {
        t100 = <li>Call and text are YES</li>;
        $[291] = t100;
      } else {
        t100 = $[291];
      }
      let t101;
      if ($[292] !== privacyNoticeOnFile) {
        t101 = privacyNoticeOnFile !== true && <p className="text-xs italic mt-1">{privacyNoticeOnFile === false ? "Privacy is not set on the Athena chart. Set it to YES during review." : "Could not verify Athena privacy. Confirm it is YES during review."}</p>;
        $[292] = privacyNoticeOnFile;
        $[293] = t101;
      } else {
        t101 = $[293];
      }
      let t102;
      if ($[294] !== t101) {
        t102 = <li>Privacy is YES{t101}</li>;
        $[294] = t101;
        $[295] = t102;
      } else {
        t102 = $[295];
      }
      let t103;
      if ($[296] === Symbol.for("react.memo_cache_sentinel")) {
        t103 = <li>If How did you hear about us is blank, set it to PHYSICIAN REFERRAL; leave any existing value unchanged.</li>;
        $[296] = t103;
      } else {
        t103 = $[296];
      }
      let t104;
      if ($[297] !== t102) {
        t104 = <li>Go to full registration and verify:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t100}{t102}{t103}</ul></li>;
        $[297] = t102;
        $[298] = t104;
      } else {
        t104 = $[298];
      }
      if ($[299] !== t104 || $[300] !== t75 || $[301] !== t76 || $[302] !== t87 || $[303] !== t91 || $[304] !== t99) {
        t22 = <div>{t74}<ol className="list-decimal list-inside space-y-3">{t75}{t76}{t77}{t87}{t91}{t99}{t104}</ol></div>;
        $[299] = t104;
        $[300] = t75;
        $[301] = t76;
        $[302] = t87;
        $[303] = t91;
        $[304] = t99;
        $[305] = t22;
      } else {
        t22 = $[305];
      }
      if ($[306] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = <h4 className="font-semibold text-foreground mb-2">Referral Processing</h4>;
        $[306] = t15;
      } else {
        t15 = $[306];
      }
      t11 = "list-decimal list-inside space-y-3";
      if ($[307] !== documentId) {
        t12 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[307] = documentId;
        $[308] = t12;
      } else {
        t12 = $[308];
      }
      let t105;
      if ($[309] !== referringProviderName) {
        t105 = <CopyableContent text={referringProviderName} />;
        $[309] = referringProviderName;
        $[310] = t105;
      } else {
        t105 = $[310];
      }
      const t106 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      let t107;
      if ($[311] !== t106) {
        t107 = <CopyableContent text={t106} />;
        $[311] = t106;
        $[312] = t107;
      } else {
        t107 = $[312];
      }
      let t108;
      if ($[313] !== referringLocationAddress) {
        t108 = <CopyableContent text={referringLocationAddress} />;
        $[313] = referringLocationAddress;
        $[314] = t108;
      } else {
        t108 = $[314];
      }
      if ($[315] !== t105 || $[316] !== t107 || $[317] !== t108) {
        t13 = <li>Referring provider: Name: {t105}, NPI:{" "}{t107}, Address:{" "}{t108}</li>;
        $[315] = t105;
        $[316] = t107;
        $[317] = t108;
        $[318] = t13;
      } else {
        t13 = $[318];
      }
      if ($[319] !== resolvedReceiving.providerLabel || $[320] !== resolvedReceiving.providerNpi || $[321] !== resolvedReceiving.providerWriteSafe) {
        t14 = <li>Receiving provider:{" "}<ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={resolvedReceiving.providerWriteSafe} /></li>;
        $[319] = resolvedReceiving.providerLabel;
        $[320] = resolvedReceiving.providerNpi;
        $[321] = resolvedReceiving.providerWriteSafe;
        $[322] = t14;
      } else {
        t14 = $[322];
      }
      t9 = "Patient Type: ";
      if ($[323] !== patientLabel) {
        t10 = <CopyableContent text={patientLabel} />;
        $[323] = patientLabel;
        $[324] = t10;
      } else {
        t10 = $[324];
      }
      t7 = "ml-4 mt-1 space-y-1 list-disc list-inside text-xs";
      t8 = sortedPastAppointments.length === 0 ? <li>No appointment history found</li> : sortedPastAppointments.map(_temp34);
    }
    $[12] = actionNote;
    $[13] = athenaData?.coverages;
    $[14] = athenaData?.patientDetails;
    $[15] = bucketLabel;
    $[16] = chartReferralAuths;
    $[17] = chartRequestLetters;
    $[18] = coverageReadback;
    $[19] = dateOfReferral;
    $[20] = decisionTrace;
    $[21] = documentId;
    $[22] = documentUploadLabels;
    $[23] = eligibilityDisplay;
    $[24] = emergencyContactName;
    $[25] = emergencyContactPhoneNumber;
    $[26] = emergencyContactRelationship;
    $[27] = followThroughEligible;
    $[28] = insuranceRequirements;
    $[29] = internalNoteToEnter;
    $[30] = isPrimaryCareProvider;
    $[31] = pastAppointments;
    $[32] = patient;
    $[33] = patientEhrId;
    $[34] = patientLabel;
    $[35] = pendingApproval;
    $[36] = primaryCareProviderAlreadyPresent;
    $[37] = primaryCoverageSummary;
    $[38] = privacyNoticeOnFile;
    $[39] = receivingAssignmentConfirmed;
    $[40] = receivingProviderName;
    $[41] = referral.diagnosisList;
    $[42] = referral.id;
    $[43] = referral.primaryCoverage;
    $[44] = referral.reasonForReferral;
    $[45] = referral.receivingLocation?.name;
    $[46] = referral.receivingProvider;
    $[47] = referral.referringLocation?.name;
    $[48] = referral.referringProvider;
    $[49] = referralAuthorization;
    $[50] = referringLocationAddress;
    $[51] = referringProviderName;
    $[52] = referringProviderSpecialty;
    $[53] = resolvedReceiving;
    $[54] = secondaryCoverageSummary;
    $[55] = task;
    $[56] = taskSteps;
    $[57] = unresolvedUpcomingProviders;
    $[58] = upcomingAppointmentProviderSummary;
    $[59] = upcomingAppointments;
    $[60] = urgency;
    $[61] = view;
    $[62] = T0;
    $[63] = dataSourceEntries;
    $[64] = hasMissingInformation;
    $[65] = missingFields;
    $[66] = t10;
    $[67] = t11;
    $[68] = t12;
    $[69] = t13;
    $[70] = t14;
    $[71] = t15;
    $[72] = t16;
    $[73] = t17;
    $[74] = t18;
    $[75] = t19;
    $[76] = t20;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
    $[80] = t24;
    $[81] = t25;
    $[82] = t7;
    $[83] = t8;
    $[84] = t9;
  } else {
    T0 = $[62];
    dataSourceEntries = $[63];
    hasMissingInformation = $[64];
    missingFields = $[65];
    t10 = $[66];
    t11 = $[67];
    t12 = $[68];
    t13 = $[69];
    t14 = $[70];
    t15 = $[71];
    t16 = $[72];
    t17 = $[73];
    t18 = $[74];
    t19 = $[75];
    t20 = $[76];
    t21 = $[77];
    t22 = $[78];
    t23 = $[79];
    t24 = $[80];
    t25 = $[81];
    t7 = $[82];
    t8 = $[83];
    t9 = $[84];
  }
  if (t25 !== Symbol.for("react.early_return_sentinel")) {
    return t25;
  }
  let t26;
  if ($[325] !== t7 || $[326] !== t8) {
    t26 = <ul className={t7}>{t8}</ul>;
    $[325] = t7;
    $[326] = t8;
    $[327] = t26;
  } else {
    t26 = $[327];
  }
  let t27;
  if ($[328] !== t10 || $[329] !== t26 || $[330] !== t9) {
    t27 = <li>{t9}{t10}{t26}</li>;
    $[328] = t10;
    $[329] = t26;
    $[330] = t9;
    $[331] = t27;
  } else {
    t27 = $[331];
  }
  let t28;
  if ($[332] !== dateOfReferral) {
    t28 = <li>Date: <CopyableContent text={dateOfReferral} /></li>;
    $[332] = dateOfReferral;
    $[333] = t28;
  } else {
    t28 = $[333];
  }
  let t29;
  if ($[334] !== internalNoteToEnter) {
    t29 = <CopyableContent text={internalNoteToEnter} />;
    $[334] = internalNoteToEnter;
    $[335] = t29;
  } else {
    t29 = $[335];
  }
  let t30;
  if ($[336] !== flags) {
    t30 = flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{flags.map(_temp35)}</ul>;
    $[336] = flags;
    $[337] = t30;
  } else {
    t30 = $[337];
  }
  let t31;
  if ($[338] !== t29 || $[339] !== t30) {
    t31 = <li>Internal Note: {t29}{t30}</li>;
    $[338] = t29;
    $[339] = t30;
    $[340] = t31;
  } else {
    t31 = $[340];
  }
  let t32;
  if ($[341] !== schedulingPriority) {
    t32 = <li>Urgency: <CopyableContent text={schedulingPriority} /></li>;
    $[341] = schedulingPriority;
    $[342] = t32;
  } else {
    t32 = $[342];
  }
  let t33;
  if ($[343] !== bucketLabel) {
    t33 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <>{NO_CONFIDENT_BUCKET_SUGGESTION}</> : <>Suggested Bucket: <CopyableContent text={SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} /></>;
    $[343] = bucketLabel;
    $[344] = t33;
  } else {
    t33 = $[344];
  }
  let t34;
  if ($[345] !== bucketLabel || $[346] !== decisionTrace) {
    t34 = decisionTrace.length > 0 && (bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{decisionTrace.map(_temp36)}</ul> : <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs"><li>{decisionTrace[decisionTrace.length - 1].label}{" "}<strong>{decisionTrace[decisionTrace.length - 1].outcome}</strong></li></ul>);
    $[345] = bucketLabel;
    $[346] = decisionTrace;
    $[347] = t34;
  } else {
    t34 = $[347];
  }
  let t35;
  if ($[348] !== t33 || $[349] !== t34) {
    t35 = <li>{t33}{t34}</li>;
    $[348] = t33;
    $[349] = t34;
    $[350] = t35;
  } else {
    t35 = $[350];
  }
  let t36;
  if ($[351] !== actionNote) {
    t36 = <li>Action note: <CopyableContent text={actionNote} /></li>;
    $[351] = actionNote;
    $[352] = t36;
  } else {
    t36 = $[352];
  }
  let t37;
  if ($[353] !== internalNoteToEnter) {
    t37 = <li>Go to quickview and add the internal note: <CopyableContent text={internalNoteToEnter} /></li>;
    $[353] = internalNoteToEnter;
    $[354] = t37;
  } else {
    t37 = $[354];
  }
  let t38;
  if ($[355] !== t11 || $[356] !== t12 || $[357] !== t13 || $[358] !== t14 || $[359] !== t27 || $[360] !== t28 || $[361] !== t31 || $[362] !== t32 || $[363] !== t35 || $[364] !== t36 || $[365] !== t37) {
    t38 = <ol className={t11}>{t12}{t13}{t14}{t27}{t28}{t31}{t32}{t35}{t36}{t37}</ol>;
    $[355] = t11;
    $[356] = t12;
    $[357] = t13;
    $[358] = t14;
    $[359] = t27;
    $[360] = t28;
    $[361] = t31;
    $[362] = t32;
    $[363] = t35;
    $[364] = t36;
    $[365] = t37;
    $[366] = t38;
  } else {
    t38 = $[366];
  }
  let t39;
  if ($[367] !== t15 || $[368] !== t38) {
    t39 = <div>{t15}{t38}</div>;
    $[367] = t15;
    $[368] = t38;
    $[369] = t39;
  } else {
    t39 = $[369];
  }
  let t40;
  let t41;
  if ($[370] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <h4 className="font-semibold text-foreground mb-2">Doc Upload</h4>;
    t41 = <p className="mb-1">Verify the following documents were uploaded:</p>;
    $[370] = t40;
    $[371] = t41;
  } else {
    t40 = $[370];
    t41 = $[371];
  }
  let t42;
  if ($[372] !== documentUploadLabels) {
    t42 = documentUploadLabels.length === 0 ? <li>N/A</li> : documentUploadLabels.map(_temp37);
    $[372] = documentUploadLabels;
    $[373] = t42;
  } else {
    t42 = $[373];
  }
  let t43;
  if ($[374] !== t42) {
    t43 = <div>{t40}{t41}<ul className="ml-4 space-y-1 list-disc list-inside">{t42}</ul></div>;
    $[374] = t42;
    $[375] = t43;
  } else {
    t43 = $[375];
  }
  let t44;
  let t45;
  if ($[376] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <h4 className="font-semibold text-foreground mb-2">Missing Information</h4>;
    t45 = <p className="font-medium text-foreground">Step 1: Missing Information Check</p>;
    $[376] = t44;
    $[377] = t45;
  } else {
    t44 = $[376];
    t45 = $[377];
  }
  let t46;
  if ($[378] !== hasMissingInformation || $[379] !== missingFields) {
    t46 = hasMissingInformation ? `Missing: ${missingFields.map(_temp38).join(", ")}` : "No missing-information letter needed.";
    $[378] = hasMissingInformation;
    $[379] = missingFields;
    $[380] = t46;
  } else {
    t46 = $[380];
  }
  let t47;
  if ($[381] !== t46) {
    t47 = <p className="mb-3">{t46}</p>;
    $[381] = t46;
    $[382] = t47;
  } else {
    t47 = $[382];
  }
  let t48;
  if ($[383] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <p className="font-medium text-foreground">Step 2: Data Source Checklist</p>;
    $[383] = t48;
  } else {
    t48 = $[383];
  }
  let t49;
  if ($[384] !== dataSourceEntries) {
    t49 = <ul className="ml-4 mb-3 space-y-1 list-disc list-inside">{dataSourceEntries.map(_temp39)}</ul>;
    $[384] = dataSourceEntries;
    $[385] = t49;
  } else {
    t49 = $[385];
  }
  let t50;
  if ($[386] !== dateOfReferral || $[387] !== hasMissingInformation || $[388] !== referringProviderName) {
    t50 = hasMissingInformation && <ol className="list-decimal list-inside space-y-2"><li>Verify the Auto-Generated document was added to the patient chart</li><li>Send the document out.<ul className="ml-4 mt-1 space-y-2 list-none"><li>a. Chart Export - sending to referring provider<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Click the hamburger menu (3 dashes) → select Chart Export.</li><li>Click the + button next to Admin Documents.</li><li>Find the "Request for Additional Information" doc and move it to the right-hand side.</li><li>Click Export Chart Sections → send to <CopyableContent text={referringProviderName} /></li></ul></li><li>c. Fill out export details<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Type <CopyableContent text={referringProviderName} /></li><li>Confirm method is set to Athena Fax and the fax number has populated.</li><li>Attention box: <CopyableContent text={referringProviderName} /></li><li>Note field: "Request for missing information."</li><li>Confirm Submit, then Save.</li></ul></li><li>d. Add Note to Patient Chart<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>"Valerie Health: Missing Information Requested <CopyableContent text={dateOfReferral} />"</li></ul></li></ul></li></ol>;
    $[386] = dateOfReferral;
    $[387] = hasMissingInformation;
    $[388] = referringProviderName;
    $[389] = t50;
  } else {
    t50 = $[389];
  }
  let t51;
  if ($[390] !== t47 || $[391] !== t49 || $[392] !== t50) {
    t51 = <div>{t44}{t45}{t47}{t48}{t49}{t50}</div>;
    $[390] = t47;
    $[391] = t49;
    $[392] = t50;
    $[393] = t51;
  } else {
    t51 = $[393];
  }
  let t52;
  if ($[394] !== t16 || $[395] !== t17 || $[396] !== t18 || $[397] !== t19 || $[398] !== t20 || $[399] !== t21 || $[400] !== t22 || $[401] !== t39 || $[402] !== t43 || $[403] !== t51) {
    t52 = <div className={t16}>{t17}{t18}{t19}{t20}{t21}{t22}{t39}{t43}{t51}</div>;
    $[394] = t16;
    $[395] = t17;
    $[396] = t18;
    $[397] = t19;
    $[398] = t20;
    $[399] = t21;
    $[400] = t22;
    $[401] = t39;
    $[402] = t43;
    $[403] = t51;
    $[404] = t52;
  } else {
    t52 = $[404];
  }
  let t53;
  if ($[405] !== T0 || $[406] !== t23 || $[407] !== t52) {
    t53 = <T0 collapsibleTitle={t23}>{t52}</T0>;
    $[405] = T0;
    $[406] = t23;
    $[407] = t52;
    $[408] = t53;
  } else {
    t53 = $[408];
  }
  let t54;
  if ($[409] !== referral.id || $[410] !== task.id) {
    t54 = <ReferralEligibilityCard referralId={referral.id} taskId={task.id} />;
    $[409] = referral.id;
    $[410] = task.id;
    $[411] = t54;
  } else {
    t54 = $[411];
  }
  let t55;
  if ($[412] !== athenaData || $[413] !== referral) {
    t55 = <AthenaReferralCards referral={referral} athenaData={athenaData} />;
    $[412] = athenaData;
    $[413] = referral;
    $[414] = t55;
  } else {
    t55 = $[414];
  }
  let t56;
  if ($[415] !== t24 || $[416] !== t53 || $[417] !== t54 || $[418] !== t55) {
    t56 = <div className={t24}>{t53}{t54}{t55}</div>;
    $[415] = t24;
    $[416] = t53;
    $[417] = t54;
    $[418] = t55;
    $[419] = t56;
  } else {
    t56 = $[419];
  }
  return t56;
}
function _temp39(entry_2) {
  return <li key={entry_2.label}>{entry_2.label}: <CopyableContent text={entry_2.value} /> ({entry_2.source})</li>;
}
function _temp38(entry_1) {
  return entry_1.label;
}
function _temp37(label) {
  return <li key={label}><CopyableContent text={label} /></li>;
}
function _temp36(entry_0, index_3) {
  return <li key={`${entry_0.label}-${index_3}`}>{entry_0.label} <strong>{entry_0.outcome}</strong></li>;
}
function _temp35(flag_0, index_2) {
  return <li key={index_2}>{flag_0}</li>;
}
function _temp34(appointment) {
  return <li key={appointment.id}>{formatAppointmentHistoryEntry(appointment)}</li>;
}
function _temp33(entry, index_1) {
  return <li key={index_1}>{entry}</li>;
}
function _temp32(flag, index_0) {
  return <li key={index_0}>{flag}</li>;
}
function _temp31(value_0, index, values) {
  return values.indexOf(value_0) === index;
}
function _temp30(value) {
  return Boolean(value);
}
function _temp29(item) {
  return item.diagnosisText;
}
function _temp28(a, b) {
  return (a.order ?? 99) - (b.order ?? 99);
}